/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faEdit,
  faSave,
  faTimes,
  faUser,
  faBriefcase,
  faCode,
  faBuilding,
  faGraduationCap,
  faCertificate,
  faLanguage,
  faLink,
  faCheckCircle,
  faPlus,
  faArrowLeft,
  faBars,
  faCamera,
  faInfoCircle,
  faExternalLinkAlt,
  faLightbulb,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import '../styles/MyProfileMobile.css';
import { buildApiUrl } from '../config/api';
import { resolveDownloadFilename } from '../utils/downloadFilename';
import { useAuth } from '../context/AuthContext';
import GoogleMapEmbed from '../components/GoogleMapEmbed';
import { ASSOCIATIONS, COMMUNITIES, ALL_OPTIONS, COUNTRIES as COMMUNITY_COUNTRIES } from '../data/communitiesAndAssociations';
import { softwareAndTools } from '../data/formOptions';
import CommunityMemberBadge from '../components/CommunityMemberBadge';
import ResumeUpload from '../components/ResumeUpload';

const MOBILE_HEADER_HEIGHT = 56;
const MOBILE_SIDEBAR_WIDTH = 260;

// Community/Association Selector Component (reusable)
const CommunityAssociationSelector = ({ value = [], onChange }) => {
  const [selectedItems, setSelectedItems] = useState(value);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState({ name: '', type: 'community', country: '' });
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedItems(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddFromDropdown = (option) => {
    const newItem = {
      name: option.name,
      type: option.type,
      country: ''
    };
    if (!selectedItems.some(item => item.name === option.name && item.type === option.type)) {
      const updated = [...selectedItems, newItem];
      setSelectedItems(updated);
      onChange(updated);
    }
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.name.trim()) {
      const newItem = {
        name: manualInput.name.trim(),
        type: manualInput.type,
        country: manualInput.country
      };
      if (!selectedItems.some(item => item.name === newItem.name && item.type === newItem.type)) {
        const updated = [...selectedItems, newItem];
        setSelectedItems(updated);
        onChange(updated);
        setManualInput({ name: '', type: 'community', country: '' });
        setShowManualForm(false);
      }
    }
  };

  const handleRemove = (index) => {
    const updated = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(updated);
    onChange(updated);
  };

  const handleUpdateCountry = (index, country) => {
    const updated = selectedItems.map((item, i) => 
      i === index ? { ...item, country } : item
    );
    setSelectedItems(updated);
    onChange(updated);
  };

  // Filter and group options
  const availableOptions = ALL_OPTIONS.filter(opt => 
    !selectedItems.some(item => item.name === opt.name && item.type === opt.type)
  );

  const filteredOptions = availableOptions.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const associations = filteredOptions.filter(opt => opt.type === 'association');
  const communities = filteredOptions.filter(opt => opt.type === 'community');

  return (
    <div style={{ width: '100%' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search communities/associations..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginTop: '4px',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredOptions.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No results found
                  </div>
                ) : (
                  <>
                    {/* Associations Section */}
                    {associations.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Associations
                        </div>
                        {associations.map((opt, idx) => (
                          <div
                            key={`assoc-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Communities Section */}
                    {communities.length > 0 && (
                      <div>
                        <div style={{
                          padding: '10px 15px',
                          background: '#f8f9fa',
                          borderTop: associations.length > 0 ? '2px solid #e9ecef' : 'none',
                          borderBottom: '1px solid #e9ecef',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Communities
                        </div>
                        {communities.map((opt, idx) => (
                          <div
                            key={`comm-${idx}`}
                            onClick={() => handleAddFromDropdown(opt)}
                            style={{
                              padding: '12px 15px',
                              cursor: 'pointer',
                              borderBottom: idx < communities.length - 1 ? '1px solid #f0f0f0' : 'none',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {opt.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowManualForm(!showManualForm)}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput.name}
              onChange={(e) => setManualInput({ ...manualInput, name: e.target.value })}
              placeholder="Enter community/association name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Type</label>
              <select
                value={manualInput.type}
                onChange={(e) => setManualInput({ ...manualInput, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="community">Community</option>
                <option value="association">Association</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
              <select
                value={manualInput.country}
                onChange={(e) => setManualInput({ ...manualInput, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.name.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.name.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.name.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput({ name: '', type: 'community', country: '' });
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Selected Communities/Associations:
          </label>
          {selectedItems.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '8px',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{item.name}</strong>
                  <span style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                    color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {item.type === 'association' ? 'Association' : 'Community'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px 8px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                  Country/Location:
                </label>
                <select
                  value={item.country || ''}
                  onChange={(e) => handleUpdateCountry(index, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value="">Select country</option>
                  {COMMUNITY_COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MyProfile = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { id: viewUserId } = useParams(); // Get userId from URL params if viewing another user
  const [viewingUser, setViewingUser] = useState(null); // User data when viewing another user's profile
  const isViewingOtherProfile = !!viewUserId && viewUserId !== currentUser?._id && viewUserId !== currentUser?.id;
  const viewerRole = (currentUser?.role || '').toLowerCase();
  
  // Use viewingUser if viewing another profile, otherwise use currentUser
  const user = isViewingOtherProfile ? viewingUser : currentUser;
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingSections, setEditingSections] = useState({});
  const [skillsDropdownOpen, setSkillsDropdownOpen] = useState(false);
  const [skillInputValue, setSkillInputValue] = useState('');
  const [selectedSoftwareTool, setSelectedSoftwareTool] = useState('');
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [locationDialogData, setLocationDialogData] = useState({
    address: '',
    currentCity: '',
    residentCountry: '',
    postalCode: '',
    latitude: '',
    longitude: ''
  });
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth > 768;
  });
  const hasAssociationVerification = useMemo(() => {
    const data = JSON.parse(sessionStorage.getItem('myProfileData') || '{}');
    return !!(data.hasAssociationVerification || (Array.isArray(data.verifiedByAssociations) && data.verifiedByAssociations.length > 0));
  }, []);
  const associationBadgeLabel = useMemo(() => {
    const data = JSON.parse(sessionStorage.getItem('myProfileData') || '{}');
    const assocNameRaw =
      data.associationName || data.communityAssociationName || data.communityAssociation || '';
    const assocName = (assocNameRaw || '').toString().trim();
    if (!assocName) return undefined;
    if (assocName.toLowerCase().includes('united asian network') || assocName.toLowerCase().includes('uan')) {
      return 'UAN Verified';
    }
    if (assocName.toLowerCase().includes('baps')) {
      return 'BAPS Verified';
    }
    return `${assocName} Verified`;
  }, []);

  const getPrimaryLocationDisplay = () => {
    if (typeof profileForm.location === 'string') {
      return profileForm.location || '—';
    } else if (profileForm.location && typeof profileForm.location === 'object') {
      const loc = profileForm.location;
      const formatted = `${loc.city || loc.currentCity || ''}, ${loc.country || loc.residentCountry || ''}`
        .replace(/^,\s*|,\s*$/g, '')
        .trim();
      if (formatted) return formatted;
      if (loc.address) return loc.address;
    }
    const fallback = `${profileForm.currentCity || ''}, ${profileForm.residentCountry || ''}`
      .replace(/^,\s*|,\s*$/g, '')
      .trim();
    return fallback || '—';
  };

  const openLocationInMaps = () => {
    const displayLocation = getPrimaryLocationDisplay();
    const lat = profileForm.latitude ? parseFloat(profileForm.latitude) : null;
    const lng = profileForm.longitude ? parseFloat(profileForm.longitude) : null;
    let mapsUrl = 'https://www.google.com/maps/search/?api=1';

    if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== null && lng !== null) {
      mapsUrl += `&query=${encodeURIComponent(`${lat},${lng}`)}`;
    } else if (displayLocation && displayLocation !== '—') {
      mapsUrl += `&query=${encodeURIComponent(displayLocation)}`;
    } else {
      return;
    }

    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };
  
  // Common skills for dropdown
  const commonSkills = [
    "3D Modeling", "A/B Testing", "Accounting Principles", "Accounting Software", "Acoustics",
    "Active Listening", "Adaptability", "Adobe Creative Suite", "Adult Learning Theory", "Agile/Scrum",
    "Agricultural Practices", "Aircraft Operation", "Algorithms", "Analysis", "Anatomy Knowledge",
    "Animal Medicine", "API Development", "Architectural Design", "Artificial Intelligence", "Assessment",
    "Assessment Design", "ATS Software", "Attention to Detail", "Audio Editing", "Audio Equipment",
    "Audio Mixing", "Auditing", "AutoCAD", "Aviation Regulations", "AWS/Azure/GCP",
    "Backup/Recovery", "Banking Regulations", "Benefits Administration", "Big Data", "Bioinformatics",
    "Biotechnology", "Blockchain", "Blueprint Reading", "Bookkeeping", "Brand Guidelines",
    "Brand Management", "Brand Strategy", "Brand Voice", "Budget Management", "Budgeting",
    "Building Codes", "Building Systems", "CAD Software", "Call Center Metrics", "Campaign Management",
    "Campaign Planning", "Candidate Assessment", "Capital Markets", "Care Planning", "Career Counseling",
    "Case Analysis", "Case Management", "Cash Handling", "Change Management", "Chemical Analysis",
    "Circuit Analysis", "Circuit Design", "Classroom Management", "Client Counseling", "Client Communication",
    "Client Relations", "Clinical Knowledge", "Closing", "Cloud Computing", "CMS Platforms",
    "Color Correction", "Color Theory", "Communication", "Communication Strategy", "Community Engagement",
    "Community Management", "Community Relations", "Compassion", "Compensation Analysis", "Compliance",
    "Computer Skills", "Confidentiality", "Conflict Resolution", "Construction Knowledge", "Construction Materials",
    "Construction Methods", "Content Creation", "Content Marketing", "Content Strategy", "Continuous Learning",
    "Contract Knowledge", "Contract Management", "Contract Negotiation", "Cooking Techniques", "Copywriting",
    "Cost Analysis", "Cost Control", "Cost Estimation", "Cost Management", "Counseling",
    "Creativity", "Credit Analysis", "Crisis Intervention", "Crisis Management", "Critical Thinking",
    "CRM Software", "Crop Management", "Crop Science", "Cryptography", "Cultural Competence",
    "Cultural Sensitivity", "Curriculum Design", "Curriculum Development", "Customer Service", "Cybersecurity Awareness",
    "Data Analysis", "Data Collection", "Data Interpretation", "Data Science", "Data Security",
    "Data Structures", "Data Visualization", "Database Design", "Database Management", "Deadline Management",
    "Deadlines", "Debugging", "Decision Making", "Design Systems", "Design Thinking",
    "Diagnosis", "Digital Literacy", "Digital Marketing", "Digital Media", "Display Design",
    "Docker", "Documentation", "Document Preparation", "DOT Regulations", "Donor Relations",
    "E-learning", "E-learning Development", "Economics", "Editing", "Education Law",
    "Efficiency Standards", "Electrical Codes", "Electrical Installation", "Electrical Systems", "Email Marketing",
    "Emergency Procedures", "Emergency Response", "Emotional Intelligence", "Empathy", "Employee Relations",
    "Energy Analysis", "Engineering Design", "Environmental Assessment", "Equipment Design", "Equipment Operation",
    "Equipment Repair", "ERP Systems", "Ethics", "Ethical Hacking", "Evaluation",
    "Event Coordination", "Event Planning", "Excel", "Experimentation", "Facilitation",
    "Fact Checking", "FEA", "Field Testing", "Field Work", "Figma/Sketch",
    "File Management", "Filing", "Financial Analysis", "Financial Modeling", "Financial Reporting",
    "Financial Statement Analysis", "First Aid", "Food Safety", "Forecasting", "Fundraising Strategy",
    "GAAP/IFRS", "GIS", "Git", "Google Analytics", "Government Processes",
    "GPS Navigation", "Grammar", "Grant Writing", "Graphic Design", "Hand/Power Tools",
    "Healthcare Management", "Healthcare Regulations", "HRIS", "HTML/CSS", "Incident Response",
    "Industry Research", "Infrastructure as Code", "Innovation", "Inspection", "Instrumentation",
    "Internet of Things (IoT)", "Interpersonal Skills", "Interviewing", "Inventory Management", "Inventory Optimization",
    "Investigation", "Investment Analysis", "Irrigation Systems", "ISO Standards", "Java",
    "Kitchen Management", "Kubernetes", "Lab Equipment Operation", "Labor Law", "Laboratory Testing",
    "Laboratory Techniques", "Language Skills", "Layout Design", "Leadership", "Lean Manufacturing",
    "Learning Management Systems", "Lease Management", "Leasing", "Legal Knowledge", "Legal Research",
    "Legal Software", "Legal Terminology", "Legal Writing", "Lesson Planning", "Linux",
    "Listening", "Literature Review", "Litigation", "Livestock Management", "Local Knowledge",
    "Lodash", "Logistics", "M&A Analysis", "Machine Learning", "Maintenance",
    "Manual Dexterity", "Manual Therapy", "Manufacturing Processes", "Market Analysis", "Market Knowledge",
    "Market Research", "Marketing", "Marketing Automation", "Marketing Strategy", "Materials Science",
    "Mathematics", "Mechanical Design", "Mechanical Systems", "Media Relations", "Medical Diagnosis",
    "Medical Equipment", "Medical Ethics", "Medication Administration", "Menu Planning", "Merchandise Display",
    "Microscopy", "Molecular Biology", "Monitoring", "Motion Graphics", "Multitasking",
    "Music Theory", "MySQL/PostgreSQL/MongoDB", "Navigation", "Needs Analysis", "Negotiation",
    "Network Security", "Networking", "News Judgment", "Office Software", "Operations Management",
    "Operations Research", "Organization", "Pain Management", "Papaparse", "Patient Assessment",
    "Patient Care", "Patient Evaluation", "Patience", "Penetration Testing", "Performance Management",
    "Performance Tuning", "Pest Management", "Phone Etiquette", "Photography", "Physical Stamina",
    "Planning", "Policy Development", "Policy Research", "POS Systems", "Power Systems",
    "PowerPoint", "Presentation", "Preventive Maintenance", "Print Production", "Problem Solving",
    "Process Improvement", "Process Optimization", "Product Knowledge", "Production Planning", "Program Knowledge",
    "Program Management", "Programming (Python, Java, C++)", "Project Management", "Project Planning", "Property Maintenance",
    "Property Valuation", "Prospecting", "Prototyping", "Public Speaking", "Python",
    "Quality Assurance", "Quality Control", "Quality Standards", "Quantum Computing", "R",
    "Record Keeping", "Recruitment", "Regulations", "Regulatory Compliance", "Regulatory Knowledge",
    "Rehabilitation", "Relationship Building", "Remote Collaboration", "Remote Support Tools", "Renewable Energy",
    "Replication", "Report Writing", "Reporting", "Research", "Research Methodology",
    "Reservation Systems", "Resource Coordination", "Retail Operations", "Retail Trends", "Revenue Management",
    "Risk Assessment", "Risk Management", "Risk Modeling", "Root Cause Analysis", "Route Planning",
    "Safe Driving", "Safety Awareness", "Safety Management", "Safety Procedures", "Safety Protocols",
    "Safety Regulations", "Safety Standards", "Sales", "Scheduling", "Scheduling Tools",
    "Scientific Writing", "Scripting", "Security", "Security Frameworks", "SEM",
    "SEO", "SheetJS", "SIEM Tools", "Simulation", "Site Analysis",
    "Site Supervision", "Six Sigma", "Social Media", "Social Media Marketing", "Social Media Strategy",
    "Software Design", "Software Systems", "Soil Analysis", "Soil Science", "Sound Recording",
    "Sourcing", "Space Planning", "Specimen Collection", "SQL", "Staff Management",
    "Staff Supervision", "Stakeholder Engagement", "Stakeholder Management", "Stakeholder Relations", "Statistical Analysis",
    "Statistical Process Control", "Statistics", "Storytelling", "Strategic Planning", "Stress Management",
    "Structural Design", "Student Advising", "Student Engagement", "Subject Expertise", "Supply Chain",
    "Supply Chain Management", "Supply Chain Strategy", "Surgery", "Sustainability", "Sustainability Practices",
    "Tax Preparation", "Teamwork", "Technical Documentation", "Technical Drawing", "Technical Knowledge",
    "Technical Support", "Technical Troubleshooting", "Technical Writing", "Technology Integration", "Tenant Relations",
    "TensorFlow/PyTorch", "Testing", "Testing Methods", "Thermodynamics", "Threat Analysis",
    "Time Management", "Training", "Training Design", "Transcription", "Transportation Planning",
    "Treatment Planning", "Trend Awareness", "Trend Forecasting", "Troubleshooting", "Typography",
    "Urban Design", "User Research", "User Testing", "Valuation", "Vehicle Maintenance",
    "Vendor Management", "Vendor Relations", "Video Editing Software", "Visual Design", "Visual Merchandising",
    "Vital Signs Monitoring", "Volunteer Management", "Warehouse Operations", "Weather Analysis", "Weather Monitoring",
    "Web Design", "Wireframing", "Wiring", "WMS Software", "Work Ethic",
    "Writing", "Zoning"
  ];
  
  const NGOS = [
    "Amref Health Africa",
    "African Wildlife Foundation",
    "World Vision Kenya",
    "Oxfam Kenya",
    "Mercy Corps Kenya",
    "Save the Children Kenya",
    "CARE Kenya",
    "Plan International Kenya",
    "Kenya Red Cross Society",
    "Action Against Hunger Kenya",
    "Catholic Relief Services Kenya",
    "International Rescue Committee Kenya",
    "Doctors Without Borders (MSF) Kenya",
    "African Population and Health Research Center (APHRC)",
    "Kenya Human Rights Commission",
    "Federation of Women Lawyers – Kenya (FIDA Kenya)",
    "Transparency International Kenya",
    "Umande Trust",
    "Carolina for Kibera (CFK Africa)",
    "Horn of Africa Development Initiative (HODI)",
    "Green Belt Movement",
    "WildlifeDirect",
    "Handicap International Kenya",
    "Water.org Kenya",
    "Living Goods Kenya",
    "Elite Ability Foundation Africa (ELAFA)"
  ];

// NGO Selector Component with Search and Manual Entry
const NGOSelector = ({ value = '', onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectNGO = (ngo) => {
    onChange(ngo);
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const handleAddManual = () => {
    if (manualInput.trim()) {
      onChange(manualInput.trim());
      setManualInput('');
      setShowManualForm(false);
    }
  };

  const handleClear = () => {
    onChange('');
    setSearchQuery('');
  };

  // Filter NGOs based on search query
  const filteredNGOS = NGOS.filter(ngo =>
    ngo.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Dropdown Selector with Search */}
      <div style={{ position: 'relative', marginBottom: '10px' }} ref={dropdownRef}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer'
              }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div style={{ padding: '8px 12px', color: '#666' }}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <input
                type="text"
                placeholder="Search or type NGO name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  borderRadius: '6px'
                }}
              />
            </div>

            {/* Custom Dropdown */}
            {dropdownOpen && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '4px',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 9999,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredNGOS.length === 0 && !searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No NGOs found
                  </div>
                ) : filteredNGOS.length === 0 && searchQuery ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    No matching NGOs found. Use "Add Manually" to add "{searchQuery}"
                  </div>
                ) : (
                  filteredNGOS.map((ngo, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectNGO(ngo)}
                      style={{
                        padding: '12px 15px',
                        cursor: 'pointer',
                        borderBottom: idx < filteredNGOS.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      {ngo}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowManualForm(!showManualForm);
              setDropdownOpen(false);
            }}
            style={{
              padding: '10px 15px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Manually
          </button>
        </div>
      </div>

      {/* Current Value Display - Show selected NGO below input field */}
      {value && (
        <div style={{
          padding: '12px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginTop: '10px',
          marginBottom: '10px',
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: '500' }}>{value}</span>
          <button
            type="button"
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              color: '#e74c3c',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px 8px'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Manual Entry Form */}
      {showManualForm && (
        <div style={{
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          marginBottom: '10px',
          background: '#f9f9f9'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              NGO Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              placeholder="Enter NGO name"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && manualInput.trim()) {
                  handleAddManual();
                }
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddManual}
              disabled={!manualInput.trim()}
              style={{
                padding: '8px 15px',
                background: manualInput.trim() ? '#27ae60' : '#bdc3c7',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: manualInput.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setManualInput('');
              }}
              style={{
                padding: '8px 15px',
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
  
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phoneNumber: '',
    phone: '',
    altPhone: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    ngo: '',
    hasDisability: 'no',
    disabilityDescription: '',
    communitiesAssociations: [],
    nationality: '',
    residentCountry: '',
    currentCity: '',
    postalCode: '',
    address: '',
    latitude: '',
    longitude: '',
    workPermit: '',
    preferredLocation1: '',
    preferredLocation2: '',
    preferredLocation3: '',
    willingToRelocate: '',
    location: '',
    professionalTitle: '',
    professionalSummary: '',
    summary: '',
    yearsOfExperience: '',
    yearsExperience: '',
    expectedSalary: '',
    jobTypePreference: '',
    jobType: '',
    availability: '',
    industry: '',
    careerLevel: '',
    noticePeriod: '',
    currentSalary: '',
    currencyPreference: '',
    travelAvailability: '',
    workLocation: '',
    membershipOrg: '',
    membershipType: '',
    membershipDate: '',
    careerObjectives: '',
    hobbies: '',
    additionalComments: '',
    participateInVolunteering: '',
    volunteeringDetails: '',
    agreeTerms: false,
    allowContact: false,
    skills: '',
    tools: '',
    profilePhoto: null
  });

  const mapQuery = useMemo(() => {
    const parts = [
      profileForm.address,
      typeof profileForm.location === 'string' ? profileForm.location : '',
      profileForm.currentCity,
      profileForm.state || profileForm.stateProvince || '',
      profileForm.residentCountry,
      profileForm.postalCode,
    ]
      .map((value) => (value || '').trim())
      .filter(Boolean);

    if (parts.length > 0) {
      const uniqueParts = [];
      parts.forEach((part) => {
        if (!uniqueParts.includes(part)) {
          uniqueParts.push(part);
        }
      });
      return uniqueParts.join(', ');
    }

    const fallback = getPrimaryLocationDisplay();
    return fallback === '—' ? '' : fallback;
  }, [
    profileForm.address,
    profileForm.location,
    profileForm.currentCity,
    profileForm.state,
    profileForm.stateProvince,
    profileForm.residentCountry,
    profileForm.postalCode,
    getPrimaryLocationDisplay
  ]);

  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [education, setEducation] = useState([]);
  const [experience, setExperience] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [professionalLinks, setProfessionalLinks] = useState({});
  const [references, setReferences] = useState([]);
  const [referenceStatusMap, setReferenceStatusMap] = useState({});
  const [verifyingReferenceIndex, setVerifyingReferenceIndex] = useState(null);
  const getReferenceStatusLabel = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'verified':
        return 'Verified';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Verification Pending';
      default:
        return 'Not Verified';
    }
  };

  const getReferenceStatusStyle = (status) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      fontWeight: 600,
      padding: '6px 12px',
      borderRadius: '999px',
      border: '1px solid transparent',
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    };

    switch ((status || '').toLowerCase()) {
      case 'verified':
        return {
          ...baseStyle,
          background: 'rgba(34,197,94,0.12)',
          borderColor: 'rgba(34,197,94,0.3)',
          color: '#15803d'
        };
      case 'rejected':
        return {
          ...baseStyle,
          background: 'rgba(239,68,68,0.12)',
          borderColor: 'rgba(239,68,68,0.3)',
          color: '#b91c1c'
        };
      case 'pending':
        return {
          ...baseStyle,
          background: 'rgba(59,130,246,0.12)',
          borderColor: 'rgba(59,130,246,0.3)',
          color: '#1d4ed8'
        };
      default:
        return {
          ...baseStyle,
          background: 'rgba(148,163,184,0.12)',
          borderColor: 'rgba(148,163,184,0.3)',
          color: '#475569'
        };
    }
  };

  const getReferenceStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'verified':
        return faCheckCircle;
      case 'rejected':
        return faTimes;
      case 'pending':
        return faInfoCircle;
      default:
        return faInfoCircle;
    }
  };

  const fetchReferenceStatuses = useCallback(async () => {
    if (isViewingOtherProfile) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(buildApiUrl('/api/references/my-references'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch reference statuses');
        return;
      }

      const data = await response.json();
      const referencesList = Array.isArray(data.references) ? data.references : [];
      const statusMap = {};

      referencesList.forEach(ref => {
        const emailKey = (ref.email || '').toLowerCase();
        if (!emailKey) return;
        statusMap[emailKey] = {
          status: ref.status || ref.verification_status || 'pending',
          verifiedAt: ref.verified_at || null,
          updatedAt: ref.updated_at || null
        };
      });

      setReferenceStatusMap(statusMap);
    } catch (error) {
      console.error('❌ Error fetching reference statuses:', error);
    }
  }, [isViewingOtherProfile]);

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

  useEffect(() => {
    if (!loading && !isViewingOtherProfile) {
      fetchReferenceStatuses();
    }
  }, [loading, isViewingOtherProfile, references.length, fetchReferenceStatuses]);

  // Calculate profile completion percentage - counting ALL 52+ fields
  const calculateProfileCompletion = () => {
    let totalFields = 0;
    let filledFields = 0;
    
    // Very strict helper to check if field has meaningful value
    const hasValue = (value) => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return false;
        return trimmed.length > 0;
      }
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') {
        return Object.values(value).some(v => v && typeof v === 'string' && v.trim().length > 0);
      }
      return false;
    };
    
    console.log(`📊 Checking Profile Completion (ALL 52+ Fields)...`);
    
    // Basic Personal Info (7 REQUIRED fields - excluding optional middleName and altPhone)
    const basicFields = {
      firstName: profileForm.firstName,
      lastName: profileForm.lastName,
      email: profileForm.email,
      phone: profileForm.phoneNumber || profileForm.phone,
      dateOfBirth: profileForm.dateOfBirth,
      gender: profileForm.gender,
      bloodGroup: profileForm.bloodGroup
    };
    totalFields += 7;
    Object.entries(basicFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Optional fields (don't count toward completion)
    // middleName, altPhone are optional
    
    // Location & Residency (10 fields)
    const locationFields = {
      nationality: profileForm.nationality,
      residentCountry: profileForm.residentCountry,
      currentCity: profileForm.currentCity,
      postalCode: profileForm.postalCode,
      address: profileForm.address,
      latitude: profileForm.latitude,
      longitude: profileForm.longitude,
      workPermit: profileForm.workPermit,
      preferredLocation1: profileForm.preferredLocation1,
      preferredLocation2: profileForm.preferredLocation2
    };
    totalFields += 10;
    Object.entries(locationFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Preferred Locations & Work Settings (5 fields)
    const workLocationFields = {
      preferredLocation3: profileForm.preferredLocation3,
      willingToRelocate: profileForm.willingToRelocate,
      workLocation: profileForm.workLocation,
      travelAvailability: profileForm.travelAvailability,
      availability: profileForm.availability
    };
    totalFields += 5;
    Object.entries(workLocationFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Professional Info (6 fields)
    const professionalFields = {
      professionalTitle: profileForm.professionalTitle,
      professionalSummary: profileForm.professionalSummary,
      yearsExperience: profileForm.yearsExperience || profileForm.yearsOfExperience,
      careerLevel: profileForm.careerLevel,
      industry: profileForm.industry,
      community: profileForm.community
    };
    totalFields += 6;
    Object.entries(professionalFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Job Preferences (6 fields)
    const jobPrefFields = {
      preferredJobTitles: profileForm.preferredJobTitles,
      jobType: profileForm.jobType,
      noticePeriod: profileForm.noticePeriod,
      currentSalary: profileForm.currentSalary,
      expectedSalary: profileForm.expectedSalary,
      currencyPreference: profileForm.currencyPreference
    };
    totalFields += 6;
    Object.entries(jobPrefFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Skills & Tools (2 fields)
    totalFields += 2;
    if (hasValue(profileForm.skills)) filledFields++;
    if (hasValue(profileForm.tools)) filledFields++;
    
    // Professional Memberships (3 fields)
    const membershipFields = {
      membershipOrg: profileForm.membershipOrg,
      membershipType: profileForm.membershipType,
      membershipDate: profileForm.membershipDate
    };
    totalFields += 3;
    Object.entries(membershipFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Additional Info (3 fields)
    const additionalFields = {
      careerObjectives: profileForm.careerObjectives,
      hobbies: profileForm.hobbies,
      additionalComments: profileForm.additionalComments
    };
    totalFields += 3;
    Object.entries(additionalFields).forEach(([key, value]) => {
      if (hasValue(value)) filledFields++;
    });
    
    // Array Sections (4 sections - count if they have entries)
    totalFields += 4;
    if (education && education.length > 0) filledFields++;
    if (experience && experience.length > 0) filledFields++;
    if (languages && languages.length > 0) filledFields++;
    if (certifications && certifications.length > 0) filledFields++;
    
    // Professional Links (1 field - at least ONE link required, not all 4)
    totalFields += 1;
    const hasAnyProfessionalLink = (professionalLinks.linkedin && professionalLinks.linkedin.trim()) ||
                                   (professionalLinks.github && professionalLinks.github.trim()) ||
                                   (professionalLinks.portfolio && professionalLinks.portfolio.trim()) ||
                                   (professionalLinks.website && professionalLinks.website.trim());
    if (hasAnyProfessionalLink) filledFields++;
    
    // References (1 section)
    totalFields += 1;
    if (references && references.length > 0) filledFields++;
    
    // Calculate percentage (Total: 46 fields - reduced from 53 by excluding 5 optional fields + combining 4 professional links into 1)
    const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
    
    // Debug: Show which fields are empty
    const emptyFields = [];
    
    // Check all sections for empty fields
    Object.entries(basicFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`Basic: ${key}`);
    });
    Object.entries(locationFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`Location: ${key}`);
    });
    Object.entries(workLocationFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`WorkLocation: ${key}`);
    });
    Object.entries(professionalFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`Professional: ${key}`);
    });
    Object.entries(jobPrefFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`JobPref: ${key}`);
    });
    if (!hasValue(profileForm.skills)) emptyFields.push('Skills');
    if (!hasValue(profileForm.tools)) emptyFields.push('Tools');
    Object.entries(membershipFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`Membership: ${key}`);
    });
    Object.entries(additionalFields).forEach(([key, value]) => {
      if (!hasValue(value)) emptyFields.push(`Additional: ${key}`);
    });
    if (!education || education.length === 0) emptyFields.push('Education (array)');
    if (!experience || experience.length === 0) emptyFields.push('Experience (array)');
    if (!languages || languages.length === 0) emptyFields.push('Languages (array)');
    if (!certifications || certifications.length === 0) emptyFields.push('Certifications (array)');
    if (!hasAnyProfessionalLink) emptyFields.push('Professional Links (at least one)');
    if (!references || references.length === 0) emptyFields.push('References (array)');
    
    console.log(`\n📊 Profile Completion Summary:`);
    console.log(`   Total Fields: ${totalFields}`);
    console.log(`   Filled Fields: ${filledFields}`);
    console.log(`   Percentage: ${percentage}%`);
    if (emptyFields.length > 0) {
      console.log(`\n❌ Empty Fields (${emptyFields.length}):`);
      emptyFields.forEach(field => console.log(`      - ${field}`));
    }
    console.log('\n');
    
    return percentage;
  };

  // Section editing functions
  const toggleSectionEdit = async (section) => {
    const isCurrentlyEditing = editingSections[section];
    
    // If canceling edit mode, refresh data to restore original values
    if (isCurrentlyEditing) {
      console.log(`🔄 Canceling edit for ${section}, restoring original data...`);
      await refreshDataAfterEdit();
    }
    
    setEditingSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleResumeUploaded = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const populateResponse = await axios.post(
        buildApiUrl('/api/jobseeker/populate-from-resume'),
        {},
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (populateResponse.data?.success) {
        setShowResumeUpload(false);
        await refreshDataAfterEdit();
        alert('✅ Resume uploaded and profile updated successfully!');
      } else {
        throw new Error(populateResponse.data?.error || 'Failed to populate profile');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      setShowResumeUpload(false);
      alert(`⚠️ Resume uploaded, but profile update failed:\n\n${errorMessage}\n\nYou can manually edit your profile.`);
    }
  };

  // Function to refresh data after edits to ensure persistence
  const refreshDataAfterEdit = async () => {
    try {
      console.log('🔄 Refreshing data after edit...');
      const res = await axios.get(buildApiUrl('/api/jobseeker/profile'), { headers: authHeaders() });
      const p = res?.data || {};
      
      // Update cache with fresh data
      sessionStorage.setItem('myProfileData', JSON.stringify(p));
      sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
      
      // Update form state with fresh data
      setProfileForm(prev => ({
        ...prev,
        // Basic Information
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        middleName: p.middleName || '',
        email: p.email || '',
        phone: p.phone || p.phoneNumber || '',
        phoneNumber: p.phoneNumber || p.phone || '',
        altPhone: p.altPhone || '',
        
        // Personal Details
        dateOfBirth: p.dateOfBirth || '',
        gender: p.gender || '',
        bloodGroup: p.bloodGroup || '',
        ngo: p.ngo || '',
        hasDisability: p.hasDisability || 'no',
        disabilityDescription: p.disabilityDescription || '',
        communitiesAssociations: (() => {
          // Parse communitiesAssociations (could be string or array)
          const value = p.communitiesAssociations;
          if (!value) {
            // Fallback to old community field if available
            return p.community ? [{ name: p.community, type: 'community', country: '' }] : [];
          }
          if (Array.isArray(value)) return value;
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        })(),
        
        // Location & Address
        nationality: p.nationality || '',
        residentCountry: p.residentCountry || '',
        currentCity: p.currentCity || '',
        postalCode: p.postalCode || '',
        address: p.address || '',
        latitude: p.latitude || '',
        longitude: p.longitude || '',
        workPermit: p.workPermit || '',
        
        // Preferred Working Locations
        preferredLocation1: p.preferredLocation1 || '',
        preferredLocation2: p.preferredLocation2 || '',
        preferredLocation3: p.preferredLocation3 || '',
        willingToRelocate: p.willingToRelocate || '',
        workLocation: p.workLocation || '',
        
        // Professional Profile
        professionalTitle: p.professionalTitle || '',
        professionalSummary: p.professionalSummary || p.summary || '',
        summary: p.summary || p.professionalSummary || '',
        yearsExperience: p.yearsExperience || p.yearsOfExperience || '',
        yearsOfExperience: p.yearsExperience || p.yearsOfExperience || '',
        careerLevel: p.careerLevel || '',
        industry: p.industry || '',
        
        // Job Preferences
        preferredJobTitles: p.preferredJobTitles || '',
        jobType: p.jobType || p.jobTypePreference || '',
        jobTypePreference: p.jobType || p.jobTypePreference || '',
        noticePeriod: p.noticePeriod || '',
        currentSalary: p.currentSalary || '',
        expectedSalary: p.expectedSalary || '',
        currencyPreference: p.currencyPreference || '',
        travelAvailability: p.travelAvailability || '',
        availability: p.availability || '',
        
        // Professional Memberships
        membershipOrg: p.membershipOrg || '',
        membershipType: p.membershipType || '',
        membershipDate: (() => {
          const date = p.membershipDate || '';
          // Convert yyyy-MM-dd to yyyy-MM for month input
          if (date && date.length === 10 && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return date.substring(0, 7); // Return yyyy-MM
          }
          return date;
        })(),
        
        // Additional Information
        careerObjectives: p.careerObjectives || '',
        hobbies: p.hobbies || '',
        additionalComments: p.additionalComments || '',
        participateInVolunteering: p.participateInVolunteering || '',
        volunteeringDetails: p.volunteeringDetails || '',
        agreeTerms: p.agreeTerms || false,
        allowContact: p.allowContact || false,
        
        // Skills & Tools
        skills: Array.isArray(p.coreSkills) ? p.coreSkills.join(', ') : (Array.isArray(p.skills) ? p.skills.join(', ') : (p.skills || '')),
        tools: Array.isArray(p.tools) ? p.tools.join(', ') : (p.tools || ''),
        profilePhoto: (() => {
          const photoPath = p.profilePhoto || p.profilePicture || p.profilePhotoPath || p.profileImage || null;
          if (!photoPath) return null;
          
          // Check if it's an old/broken path and skip it
          const pathStr = String(photoPath);
          if (pathStr.includes('jobseeker_profiles/photos')) {
            console.log('⚠️ Skipping old photo path in refreshDataAfterEdit:', pathStr);
            return null; // Return null to use photoPreview or initials
          }
          
          if (typeof photoPath === 'string' && (photoPath.startsWith('http://') || photoPath.startsWith('https://'))) {
            return photoPath;
          }
          
          // Normalize path separators (handle Windows backslashes)
          let normalizedPath = typeof photoPath === 'string' ? photoPath.replace(/\\/g, '/') : photoPath;
          
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace(/^uploads\//, '');
            return buildApiUrl(`/api/users/uploads/${uploadPath}`);
          }
          if (normalizedPath.startsWith('/')) {
            if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace(/^\/uploads\//, '');
              return buildApiUrl(`/api/users/uploads/${uploadPath}`);
            }
            const baseUrl = buildApiUrl('');
            const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
            return `${cleanBaseUrl}${normalizedPath}`;
          }
          
          // If it has path separators, assume it's in uploads
          if (normalizedPath.includes('/')) {
            return buildApiUrl(`/api/users/uploads/${normalizedPath}`);
          }
          
          return photoPath;
        })()
      }));
      
      // Update array fields
      setEducation(Array.isArray(p.educationEntries) ? p.educationEntries : (Array.isArray(p.education) ? p.education : []));
      setExperience(Array.isArray(p.experienceEntries) ? p.experienceEntries : (Array.isArray(p.experience) ? p.experience : (Array.isArray(p.workExperience) ? p.workExperience : [])));
      setLanguages(Array.isArray(p.languages) ? p.languages.map(l => (typeof l === 'string' ? { language: l, proficiency: '' } : l)) : []);
      setCertifications(Array.isArray(p.certificationEntries) ? p.certificationEntries : (Array.isArray(p.certifications) ? p.certifications : []));
      const refreshedReferencesData = Array.isArray(p.referenceEntries) ? p.referenceEntries : (Array.isArray(p.references) ? p.references : []);
      setReferences(refreshedReferencesData);
      console.log(`📞 Refreshed References: ${refreshedReferencesData.length} items`, refreshedReferencesData);
      
      // Update professional links - handle both array and object formats
      const links = p.professionalLinks || {};
      
      // Convert array format to object format if needed
      let linksObject = {};
      if (Array.isArray(links)) {
        // Array format: [{type: 'LinkedIn', url: '...'}, {type: 'GitHub', url: '...'}]
        links.forEach(link => {
          const type = link.type?.toLowerCase();
          if (type === 'linkedin') linksObject.linkedin = link.url;
          else if (type === 'github') linksObject.github = link.url;
          else if (type === 'portfolio') linksObject.portfolio = link.url;
          else if (type === 'personal website' || type === 'website') linksObject.website = link.url;
        });
      } else if (typeof links === 'object') {
        // Object format: {linkedin: '...', github: '...'}
        linksObject = links;
      }
      
      setProfessionalLinks({
        linkedin: linksObject.linkedin || linksObject.linkedinUrl || '',
        github: linksObject.github || linksObject.githubUrl || '',
        portfolio: linksObject.portfolio || linksObject.portfolioUrl || '',
        website: linksObject.website || linksObject.websiteUrl || ''
      });
      
      console.log('✅ Data refreshed successfully after edit');
      console.log('🔍 Refreshed Volunteering Data:', {
        participateInVolunteering: p.participateInVolunteering,
        volunteeringDetails: p.volunteeringDetails
      });
    } catch (error) {
      console.error('❌ Error refreshing data after edit:', error);
    }
  };

  // Function to ensure array fields are properly updated in form state
  const updateArrayFieldsInForm = () => {
    setProfileForm(prev => ({
      ...prev,
      // Update skills and tools from arrays
      skills: Array.isArray(profileForm.skills) ? profileForm.skills.join(', ') : (profileForm.skills || ''),
      tools: Array.isArray(profileForm.tools) ? profileForm.tools.join(', ') : (profileForm.tools || ''),
    }));
  };

  // Function to ensure array fields are properly saved
  const ensureArrayFieldsSaved = (payload) => {
    // Ensure all array fields are properly included
    const arrayFields = {
      education: education || [],
      experience: experience || [],
      languages: languages || [],
      certifications: certifications || [],
      references: references || [],
      professionalLinks: professionalLinks || {}
    };

    return {
      ...payload,
      ...arrayFields,
      // Also include with different naming conventions for backend compatibility
      educationEntries: education || [],
      experienceEntries: experience || [],
      workExperience: experience || [],
      certificationEntries: certifications || [],
      referenceEntries: references || [],
      languageProficiency: languages || []
    };
  };

  // Data validation function to ensure 100% accuracy
  const validateProfileData = (data) => {
    const validation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Check required fields
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 
      'gender', 'nationality', 'currentCity', 'workPermit',
      'preferredLocation1', 'willingToRelocate', 'workLocation',
      'professionalTitle', 'yearsExperience', 'industry'
    ];

    requiredFields.forEach(field => {
      if (!data[field] || data[field] === '') {
        validation.errors.push(`Missing required field: ${field}`);
        validation.isValid = false;
      }
    });

    // Check array fields
    const arrayFields = ['education', 'experience', 'languages', 'skills'];
    arrayFields.forEach(field => {
      if (!Array.isArray(data[field]) || data[field].length === 0) {
        validation.warnings.push(`Empty array field: ${field}`);
      }
    });

    // Check professional links structure
    if (data.professionalLinks && typeof data.professionalLinks === 'object') {
      const linkFields = ['linkedin', 'github', 'portfolio', 'website'];
      const hasAnyLink = linkFields.some(field => data.professionalLinks[field]);
      if (!hasAnyLink) {
        validation.warnings.push('No professional links provided');
      }
    }

    console.log('🔍 Data validation result:', validation);
    return validation;
  };

  const openLocationDialog = () => {
    setLocationDialogData({
      address: profileForm.address || '',
      currentCity: profileForm.currentCity || '',
      residentCountry: profileForm.residentCountry || '',
      postalCode: profileForm.postalCode || '',
      latitude: profileForm.latitude || '',
      longitude: profileForm.longitude || ''
    });
    setLocationDialogOpen(true);
  };

  const closeLocationDialog = () => {
    setLocationDialogOpen(false);
  };

  const handleLocationDialogSave = async () => {
    setSaving(true);
    try {
      // Update profileForm with dialog data first
      const updatedProfileForm = {
        ...profileForm,
        address: locationDialogData.address,
        currentCity: locationDialogData.currentCity,
        residentCountry: locationDialogData.residentCountry,
        postalCode: locationDialogData.postalCode,
        latitude: locationDialogData.latitude,
        longitude: locationDialogData.longitude
      };
      
      // Update state
      setProfileForm(updatedProfileForm);
      
      // Build payload with the updated location data
      const payload = {
        // Basic Information - Map all possible field names
        firstName: updatedProfileForm.firstName || '',
        middleName: updatedProfileForm.middleName || '',
        lastName: updatedProfileForm.lastName || '',
        fullName: `${updatedProfileForm.firstName || ''} ${updatedProfileForm.middleName || ''} ${updatedProfileForm.lastName || ''}`.trim(),
        email: updatedProfileForm.email || '',
        phone: updatedProfileForm.phone || updatedProfileForm.phoneNumber || '',
        phoneNumber: updatedProfileForm.phone || updatedProfileForm.phoneNumber || '',
        altPhone: updatedProfileForm.altPhone || '',
        
        // Personal Details
        dateOfBirth: updatedProfileForm.dateOfBirth || '',
        gender: updatedProfileForm.gender || '',
        bloodGroup: updatedProfileForm.bloodGroup || '',
        community: updatedProfileForm.community || '',
        
        // Location & Address - Use dialog data
        nationality: updatedProfileForm.nationality || '',
        residentCountry: locationDialogData.residentCountry || '',
        currentCity: locationDialogData.currentCity || '',
        postalCode: locationDialogData.postalCode || '',
        address: locationDialogData.address || '',
        latitude: locationDialogData.latitude || '',
        longitude: locationDialogData.longitude || '',
        workPermit: updatedProfileForm.workPermit || '',
        
        // Preferred Working Locations
        preferredLocation1: updatedProfileForm.preferredLocation1 || '',
        preferredLocation2: updatedProfileForm.preferredLocation2 || '',
        preferredLocation3: updatedProfileForm.preferredLocation3 || '',
        willingToRelocate: updatedProfileForm.willingToRelocate || '',
        workLocation: updatedProfileForm.workLocation || '',
        
        // Professional Profile
        professionalTitle: updatedProfileForm.professionalTitle || '',
        professionalSummary: updatedProfileForm.professionalSummary || updatedProfileForm.summary || '',
        summary: updatedProfileForm.professionalSummary || updatedProfileForm.summary || '',
        yearsExperience: updatedProfileForm.yearsExperience || updatedProfileForm.yearsOfExperience || '',
        yearsOfExperience: updatedProfileForm.yearsExperience || updatedProfileForm.yearsOfExperience || '',
        careerLevel: updatedProfileForm.careerLevel || '',
        industry: updatedProfileForm.industry || '',
        
        // Job Preferences
        preferredJobTitles: updatedProfileForm.preferredJobTitles || '',
        jobType: updatedProfileForm.jobType || updatedProfileForm.jobTypePreference || '',
        jobTypePreference: updatedProfileForm.jobType || updatedProfileForm.jobTypePreference || '',
        noticePeriod: updatedProfileForm.noticePeriod || '',
        currentSalary: updatedProfileForm.currentSalary || '',
        expectedSalary: updatedProfileForm.expectedSalary || '',
        currencyPreference: updatedProfileForm.currencyPreference || '',
        travelAvailability: updatedProfileForm.travelAvailability || '',
        availability: updatedProfileForm.availability || '',
        
        // Professional Memberships
        membershipOrg: updatedProfileForm.membershipOrg || '',
        membershipType: updatedProfileForm.membershipType || '',
        membershipDate: updatedProfileForm.membershipDate || '',
        
        // Additional Information
        careerObjectives: updatedProfileForm.careerObjectives || '',
        hobbies: updatedProfileForm.hobbies || '',
        additionalComments: updatedProfileForm.additionalComments || '',
        participateInVolunteering: updatedProfileForm.participateInVolunteering || '',
        volunteeringDetails: updatedProfileForm.volunteeringDetails || '',
        agreeTerms: updatedProfileForm.agreeTerms || false,
        allowContact: updatedProfileForm.allowContact || false,
        
        // Skills & Tools - Convert to arrays
        skills: updatedProfileForm.skills ? updatedProfileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        coreSkills: updatedProfileForm.skills ? updatedProfileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        tools: updatedProfileForm.tools ? updatedProfileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        softwareTools: updatedProfileForm.tools ? updatedProfileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        
        // Array Fields - Use both naming conventions for compatibility
        education: education,
        educationEntries: education,
        experience: experience,
        experienceEntries: experience,
        workExperience: experience,
        languages: languages,
        languageProficiency: languages,
        certifications: certifications,
        certificationEntries: certifications,
        references: references,
        referenceEntries: references,
        professionalLinks: professionalLinks,
        
        // Profile completion status
        profileCompleted: true,
        hasCompletedProfile: true
      };
      
      // Ensure array fields are properly included
      const finalPayload = ensureArrayFieldsSaved(payload);
      
      console.log(`💾 Saving location from dialog:`, finalPayload);
      console.log(`📍 Location data:`, {
        address: locationDialogData.address,
        currentCity: locationDialogData.currentCity,
        residentCountry: locationDialogData.residentCountry,
        postalCode: locationDialogData.postalCode,
        latitude: locationDialogData.latitude,
        longitude: locationDialogData.longitude
      });
      
      const response = await axios.put(buildApiUrl('/api/user/profile'), finalPayload, { 
        headers: { 
          ...authHeaders(), 
          'Content-Type': 'application/json' 
        } 
      });
      
      console.log(`✅ Location saved successfully:`, response.data);
      
      // Close dialog after successful save
      setLocationDialogOpen(false);
      
      // Show success message
      alert('Residency section saved successfully!');
      
      // Reload profile to get updated data
      await refreshDataAfterEdit();
      
    } catch (error) {
      console.error('❌ Error saving location:', error);
      alert('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50KB limit like intern)
      if (file.size > 50 * 1024) {
        alert('Profile photo must be 50KB or smaller. Please compress your image.');
        e.target.value = '';
        return;
      }
      
      setSelectedPhotoFile(file);
      // Create preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Auto-upload the photo immediately (like intern does)
      try {
        setSaving(true);
        console.log('📸 Auto-uploading photo immediately...');
        
        const formData = new FormData();
        formData.append('profilePhoto', file);
        
        // Include minimal profile data to ensure the save works
        const minimalPayload = {
          ...profileForm,
          educationEntries: education,
          experienceEntries: experience,
          languages: languages,
          certifications: certifications,
          references: references,
          professionalLinks: professionalLinks
        };
        
        // Append all form fields as JSON strings for complex data
        Object.keys(minimalPayload).forEach(key => {
          const value = minimalPayload[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        const response = await axios.put(buildApiUrl('/api/user/profile'), formData, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.status === 200) {
          console.log('✅ Photo uploaded successfully!', response.data);
          
          // Update profileForm with the new photo path if returned
          if (response.data.profileImage || response.data.profilePhotoPath) {
            const photoPath = response.data.profileImage || response.data.profilePhotoPath;
            console.log('📸 New photo path from server:', photoPath);
            if (photoPath && typeof photoPath === 'string') {
              let normalizedPath = photoPath.replace(/\\/g, '/');
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace('uploads/', '');
                const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
                setPhotoPreview(fullUrl);
              } else if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace('/uploads/', '');
                const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
                setPhotoPreview(fullUrl);
              } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                console.log('📸 Setting profilePhoto to full URL:', normalizedPath);
                setProfileForm(p => ({ ...p, profilePhoto: normalizedPath }));
                setPhotoPreview(normalizedPath);
              } else {
                const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
                console.log('📸 Setting profilePhoto to:', fullUrl);
                setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
                setPhotoPreview(fullUrl);
              }
            }
          }
          
          // Clear the file selection after successful upload
          setSelectedPhotoFile(null);
          if (e.target) {
            e.target.value = '';
          }
          
          // Update cache
          const updatedCache = {
            ...JSON.parse(sessionStorage.getItem('myProfileData') || '{}')
          };
          if (response.data.profileImage) {
            updatedCache.profileImage = response.data.profileImage;
            updatedCache.profilePhotoPath = response.data.profileImage;
            updatedCache.profilePhoto = response.data.profileImage;
          }
          sessionStorage.setItem('myProfileData', JSON.stringify(updatedCache));
          sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
        }
      } catch (error) {
        console.error('❌ Error uploading photo:', error);
        console.error('Error details:', error.response?.data);
        alert('❌ Error uploading photo. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      console.log(`💾 Saving ${section} section...`);
      console.log(`🔍 Current profileForm state:`, profileForm);
      
      const payload = {
        // Basic Information - Map all possible field names
        firstName: profileForm.firstName || '',
        middleName: profileForm.middleName || '',
        lastName: profileForm.lastName || '',
        fullName: `${profileForm.firstName || ''} ${profileForm.middleName || ''} ${profileForm.lastName || ''}`.trim(),
        email: profileForm.email || '',
        phone: profileForm.phone || profileForm.phoneNumber || '',
        phoneNumber: profileForm.phone || profileForm.phoneNumber || '',
        altPhone: profileForm.altPhone || '',
        
        // Personal Details
        dateOfBirth: profileForm.dateOfBirth || '',
        gender: profileForm.gender || '',
        bloodGroup: profileForm.bloodGroup || '',
        ngo: profileForm.ngo || '',
        hasDisability: profileForm.hasDisability || 'no',
        disabilityDescription: profileForm.disabilityDescription || '',
        communitiesAssociations: profileForm.communitiesAssociations || [],
        
        // Location & Address
        nationality: profileForm.nationality || '',
        residentCountry: profileForm.residentCountry || '',
        currentCity: profileForm.currentCity || '',
        postalCode: profileForm.postalCode || '',
        address: profileForm.address || '',
        latitude: profileForm.latitude || '',
        longitude: profileForm.longitude || '',
        workPermit: profileForm.workPermit || '',
        
        // Preferred Working Locations
        preferredLocation1: profileForm.preferredLocation1 || '',
        preferredLocation2: profileForm.preferredLocation2 || '',
        preferredLocation3: profileForm.preferredLocation3 || '',
        willingToRelocate: profileForm.willingToRelocate || '',
        workLocation: profileForm.workLocation || '',
        
        // Professional Profile
        professionalTitle: profileForm.professionalTitle || '',
        professionalSummary: profileForm.professionalSummary || profileForm.summary || '',
        summary: profileForm.professionalSummary || profileForm.summary || '',
        yearsExperience: profileForm.yearsExperience || profileForm.yearsOfExperience || '',
        yearsOfExperience: profileForm.yearsExperience || profileForm.yearsOfExperience || '',
        careerLevel: profileForm.careerLevel || '',
        industry: profileForm.industry || '',
        
        // Job Preferences
        preferredJobTitles: profileForm.preferredJobTitles || '',
        jobType: profileForm.jobType || profileForm.jobTypePreference || '',
        jobTypePreference: profileForm.jobType || profileForm.jobTypePreference || '',
        noticePeriod: profileForm.noticePeriod || '',
        currentSalary: profileForm.currentSalary || '',
        expectedSalary: profileForm.expectedSalary || '',
        currencyPreference: profileForm.currencyPreference || '',
        travelAvailability: profileForm.travelAvailability || '',
        availability: profileForm.availability || '',
        
        // Professional Memberships
        membershipOrg: profileForm.membershipOrg || '',
        membershipType: profileForm.membershipType || '',
        membershipDate: profileForm.membershipDate || '',
        
        // Additional Information
        careerObjectives: profileForm.careerObjectives || '',
        hobbies: profileForm.hobbies || '',
        additionalComments: profileForm.additionalComments || '',
        participateInVolunteering: profileForm.participateInVolunteering || '',
        volunteeringDetails: profileForm.volunteeringDetails || '',
        agreeTerms: profileForm.agreeTerms || false,
        allowContact: profileForm.allowContact || false,
        
        // Skills & Tools - Convert to arrays
        skills: profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        coreSkills: profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        tools: profileForm.tools ? profileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        softwareTools: profileForm.tools ? profileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        
        // Array Fields - Use both naming conventions for compatibility
        education: education,
        educationEntries: education,
        experience: experience,
        experienceEntries: experience,
        workExperience: experience,
        languages: languages,
        languageProficiency: languages,
        certifications: certifications,
        certificationEntries: certifications,
        references: references,
        referenceEntries: references,
        professionalLinks: professionalLinks,
        
        // Profile completion status
        profileCompleted: true,
        hasCompletedProfile: true
      };
      
      // Ensure array fields are properly included
      const finalPayload = ensureArrayFieldsSaved(payload);
      
      console.log(`📤 Sending payload for ${section}:`, finalPayload);
      console.log(`🩸 Blood Group in payload:`, payload.bloodGroup);
      console.log(`👤 Demographics fields in payload:`, {
        bloodGroup: payload.bloodGroup,
        ngo: payload.ngo,
        dateOfBirth: payload.dateOfBirth,
        gender: payload.gender,
        communitiesAssociations: payload.communitiesAssociations,
        nationality: payload.nationality,
        currentCity: payload.currentCity
      });
      console.log(`📋 Array fields in payload:`, {
        education: payload.education?.length || 0,
        experience: payload.experience?.length || 0,
        languages: payload.languages?.length || 0,
        certifications: payload.certifications?.length || 0,
        references: payload.references?.length || 0,
        skills: payload.skills?.length || 0,
        tools: payload.tools?.length || 0
      });
      console.log(`🔗 Professional links in payload:`, finalPayload.professionalLinks);
      
      // No validation - allow saving with any fields filled or empty
      console.log('💾 Saving profile without validation checks...');
      
      // If there's a photo file, use FormData; otherwise use JSON
      let response;
      if (selectedPhotoFile) {
        console.log('📸 Uploading profile with photo...');
        const formData = new FormData();
        formData.append('profilePhoto', selectedPhotoFile);
        
        // Append all profile data as JSON string (backend will parse it)
        formData.append('profileData', JSON.stringify(finalPayload));
        
        // Also append each field individually for backward compatibility
        Object.keys(finalPayload).forEach(key => {
          const value = finalPayload[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              // For objects, stringify them
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              // For arrays, stringify them
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        response = await axios.put(buildApiUrl('/api/user/profile'), formData, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Regular JSON save
        response = await axios.put(buildApiUrl('/api/user/profile'), finalPayload, { 
          headers: { 
            ...authHeaders(), 
            'Content-Type': 'application/json' 
          } 
        });
      }
      
      console.log(`✅ ${section} section saved successfully:`, response.data);
      
      // Update profileForm with the new photo path if returned
      if (selectedPhotoFile && (response.data.profileImage || response.data.profilePhotoPath)) {
        const photoPath = response.data.profileImage || response.data.profilePhotoPath;
        console.log('📸 New photo path from server:', photoPath);
        if (photoPath && typeof photoPath === 'string') {
          let normalizedPath = photoPath.replace(/\\/g, '/');
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace('uploads/', '');
            const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            // Also update photoPreview to the new URL so it displays immediately
            setPhotoPreview(fullUrl);
          } else if (normalizedPath.startsWith('/uploads/')) {
            const uploadPath = normalizedPath.replace('/uploads/', '');
            const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            setPhotoPreview(fullUrl);
          } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            console.log('📸 Setting profilePhoto to full URL:', normalizedPath);
            setProfileForm(p => ({ ...p, profilePhoto: normalizedPath }));
            setPhotoPreview(normalizedPath);
          } else {
            // Assume it's a relative path
            const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            setPhotoPreview(fullUrl);
          }
        }
        // Clear the file selection after successful upload
        setSelectedPhotoFile(null);
      }
      console.log(`🔍 Saved data - References:`, response.data?.references || response.data?.referenceEntries);
      console.log(`🔍 Saved data - Certifications:`, response.data?.certifications || response.data?.certificationEntries);
      console.log(`🔍 Saved data - Experience:`, response.data?.experience || response.data?.experienceEntries);
      console.log(`🔍 Saved data - Education:`, response.data?.education || response.data?.educationEntries);
      console.log(`🔍 Saved data - Volunteering:`, {
        participateInVolunteering: response.data?.participateInVolunteering,
        volunteeringDetails: response.data?.volunteeringDetails
      });
      
      // Update cache with the saved data instead of clearing it
      const updatedCache = {
        ...JSON.parse(sessionStorage.getItem('myProfileData') || '{}'),
        ...finalPayload
      };
      if (selectedPhotoFile && response.data.profileImage) {
        updatedCache.profileImage = response.data.profileImage;
        updatedCache.profilePhotoPath = response.data.profileImage;
        updatedCache.profilePhoto = response.data.profileImage;
      }
      sessionStorage.setItem('myProfileData', JSON.stringify(updatedCache));
      sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
      
      // Show success message
      alert(`${section.charAt(0).toUpperCase() + section.slice(1)} section saved successfully!`);
      
      // Refresh data immediately to ensure we see the saved data
      console.log(`🔄 Refreshing data after save to verify persistence...`);
      await refreshDataAfterEdit();
      
      // Verify data was actually saved
      console.log(`✅ After refresh - References count:`, references.length);
      console.log(`✅ After refresh - Certifications count:`, certifications.length);
      
      // Update AuthContext user data if firstName or lastName changed
      if (user && (finalPayload.firstName !== user.firstName || finalPayload.lastName !== user.lastName)) {
        const updatedUser = {
          ...user,
          firstName: finalPayload.firstName,
          lastName: finalPayload.lastName,
          fullName: `${finalPayload.firstName || ''} ${finalPayload.middleName || ''} ${finalPayload.lastName || ''}`.trim()
        };
        // Store in localStorage to persist across page reloads
        localStorage.setItem('userFirstName', finalPayload.firstName);
        localStorage.setItem('userLastName', finalPayload.lastName);
        console.log('📝 Updated user data in AuthContext and localStorage');
      }
      
      // Close edit mode after successful save and refresh
      setEditingSections(prev => ({ ...prev, [section]: false }));
      
    } catch (error) {
      console.error(`❌ Error saving ${section} section:`, error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show error message
      alert(`Error saving ${section} section: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addSkill = (skill) => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;
    
    const currentSkills = profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
    if (!currentSkills.includes(trimmedSkill)) {
      setProfileForm(prev => ({
        ...prev,
        skills: [...currentSkills, trimmedSkill].join(', ')
      }));
    }
    setSkillInputValue('');
    setSkillsDropdownOpen(false);
  };

  const removeSkill = (skillToRemove) => {
    const currentSkills = profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [];
    setProfileForm(prev => ({
      ...prev,
      skills: currentSkills.filter(skill => skill !== skillToRemove).join(', ')
    }));
  };

  // Close skills dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (skillsDropdownOpen && !event.target.closest('.skills-dropdown-container')) {
        setSkillsDropdownOpen(false);
        // Optionally clear input when clicking outside - comment out if you want to keep the typed text
        // setSkillInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [skillsDropdownOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (isMobileView) {
      setMobileSidebarOpen(false);
    } else {
      setMobileSidebarOpen(true);
    }
  }, [isMobileView]);

  // Recalculate profile completion when data changes (especially important when viewing other user's profile)
  useEffect(() => {
    // If viewing another user's profile, recalculate completion from frontend state
    // since backend may not provide it
    if (isViewingOtherProfile && !loading) {
      const calculated = calculateProfileCompletion();
      setProfileCompletionPercentage(calculated);
    }
  }, [profileForm, education, experience, languages, certifications, professionalLinks, references, loading, isViewingOtherProfile]);

  // Fetch another user's profile when viewing
  useEffect(() => {
    const loadOtherUserProfile = async () => {
      if (!viewUserId || !isViewingOtherProfile) return;
      
      try {
        setLoading(true);
        console.log(`🔄 Loading profile for user ID: ${viewUserId}`);
        
        const res = await axios.get(buildApiUrl(`/api/users/profile/${viewUserId}`), { headers: authHeaders() });
        const userData = res?.data || {};
        
        setViewingUser(userData);
        
        // Populate form with the viewed user's data (read-only)
        setProfileForm(prev => ({
          ...prev,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          middleName: userData.middleName || '',
          email: userData.email || '',
          phoneNumber: userData.phone || userData.phoneNumber || '',
          phone: userData.phone || userData.phoneNumber || '',
          altPhone: userData.altPhone || '',
          dateOfBirth: userData.dateOfBirth || '',
          gender: userData.gender || '',
          bloodGroup: userData.bloodGroup || '',
          community: userData.community || userData.primary_community || '',
          nationality: userData.nationality || '',
          residentCountry: userData.residentCountry || '',
          currentCity: userData.currentCity || '',
          postalCode: userData.postalCode || '',
          address: userData.address || '',
          latitude: userData.latitude || '',
          longitude: userData.longitude || '',
          workPermit: userData.workPermit || '',
          preferredLocation1: userData.preferredLocation1 || '',
          preferredLocation2: userData.preferredLocation2 || '',
          preferredLocation3: userData.preferredLocation3 || '',
          willingToRelocate: userData.willingToRelocate || '',
          location: (() => {
            // Handle location - convert object to string if needed
            if (typeof userData.location === 'string') {
              return userData.location;
            } else if (userData.location && typeof userData.location === 'object') {
              const loc = userData.location;
              return `${loc.city || loc.currentCity || ''}, ${loc.country || loc.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || 
                     loc.address || 
                     `${userData.currentCity || ''}, ${userData.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || '';
            } else {
              return `${userData.currentCity || ''}, ${userData.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
            }
          })(),
          professionalTitle: userData.professionalTitle || '',
          professionalSummary: userData.professionalSummary || userData.summary || '',
          summary: userData.summary || userData.professionalSummary || '',
          yearsOfExperience: userData.yearsExperience || userData.yearsOfExperience || '',
          yearsExperience: userData.yearsExperience || userData.yearsOfExperience || '',
          expectedSalary: userData.expectedSalary || '',
          jobTypePreference: userData.jobTypePreference || '',
          availability: userData.availability || '',
          industry: userData.industry || '',
          careerLevel: userData.careerLevel || '',
          noticePeriod: userData.noticePeriod || '',
          currentSalary: userData.currentSalary || '',
          currencyPreference: userData.currencyPreference || '',
          travelAvailability: userData.travelAvailability || '',
          workLocation: userData.workLocation || '',
          membershipOrg: userData.membershipOrg || '',
          membershipType: userData.membershipType || '',
          membershipDate: userData.membershipDate || '',
          careerObjectives: userData.careerObjectives || '',
          hobbies: userData.hobbies || '',
          additionalComments: userData.additionalComments || '',
          agreeTerms: userData.agreeTerms || false,
          allowContact: userData.allowContact || false,
          jobType: userData.jobType || '',
          preferredJobTitles: userData.preferredJobTitles || '',
          skills: Array.isArray(userData.coreSkills) ? userData.coreSkills.join(', ') : (Array.isArray(userData.skills) ? userData.skills.join(', ') : (userData.skills || '')),
          tools: Array.isArray(userData.tools) ? userData.tools.join(', ') : (userData.tools || ''),
          profilePhoto: (() => {
            // Check multiple possible field names and formats
            const photoPath = userData.profilePhoto || 
                              userData.profilePicture || 
                              userData.profilePhotoPath || 
                              userData.profileImage || null;
            
            if (!photoPath) return null;
            
            // Check if it's an old/broken path and skip it
            const pathStr = String(photoPath);
            if (pathStr.includes('jobseeker_profiles/photos')) {
              console.log('⚠️ Skipping old photo path in loadOtherUserProfile:', pathStr);
              return null; // Return null to use initials
            }
            
            // If it's already a full URL (starts with http:// or https://), use it directly
            if (typeof photoPath === 'string' && (photoPath.startsWith('http://') || photoPath.startsWith('https://'))) {
              return photoPath;
            }
            
            // Normalize path separators (handle Windows backslashes)
            let normalizedPath = typeof photoPath === 'string' ? photoPath.replace(/\\/g, '/') : photoPath;
            
            // If it's a relative path starting with 'uploads/', convert to API endpoint URL
            if (normalizedPath.startsWith('uploads/')) {
              // Remove 'uploads/' prefix to get the relative path within uploads
              const uploadPath = normalizedPath.replace(/^uploads\//, '');
              return buildApiUrl(`/api/users/uploads/${uploadPath}`);
            }
            
            // If it starts with /uploads/, handle it
            if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace(/^\/uploads\//, '');
              return buildApiUrl(`/api/users/uploads/${uploadPath}`);
            }
            
            // If it's already a path starting with /, construct URL
            if (normalizedPath.startsWith('/')) {
              const baseUrl = buildApiUrl('');
              const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
              return `${cleanBaseUrl}${normalizedPath}`;
            }
            
            // If it doesn't start with uploads/ but might be a relative path, try adding uploads/
            if (normalizedPath.includes('/')) {
              // Assume it's in uploads if it has path separators
              return buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            
            // Return as-is if it's already a valid path
            return photoPath;
          })()
        }));
        
        // Normalize education entries - convert location objects to strings
        const normalizedEducation = (Array.isArray(userData.educationEntries) ? userData.educationEntries : (Array.isArray(userData.education) ? userData.education : [])).map(ed => {
          if (ed.location && typeof ed.location === 'object') {
            const loc = ed.location;
            return {
              ...ed,
              location: `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '',
              institutionLocation: ed.institutionLocation || (typeof ed.location === 'string' ? ed.location : `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '')
            };
          }
          return {
            ...ed,
            institutionLocation: ed.institutionLocation || (typeof ed.location === 'string' ? ed.location : '')
          };
        });
        setEducation(normalizedEducation);
        
        // Normalize experience entries - convert location objects to strings
        const normalizedExperience = (Array.isArray(userData.experienceEntries) ? userData.experienceEntries : (Array.isArray(userData.experience) ? userData.experience : (Array.isArray(userData.workExperience) ? userData.workExperience : []))).map(ex => {
          if (ex.location && typeof ex.location === 'object') {
            const loc = ex.location;
            return {
              ...ex,
              location: `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '',
              companyLocation: ex.companyLocation || (typeof ex.location === 'string' ? ex.location : `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '')
            };
          }
          return {
            ...ex,
            companyLocation: ex.companyLocation || (typeof ex.location === 'string' ? ex.location : '')
          };
        });
        setExperience(normalizedExperience);
        setLanguages(Array.isArray(userData.languages) ? userData.languages.map(l => (typeof l === 'string' ? { language: l, proficiency: '' } : l)) : []);
        setCertifications(Array.isArray(userData.certificationEntries) ? userData.certificationEntries : (Array.isArray(userData.certifications) ? userData.certifications : []));
        setReferences(Array.isArray(userData.referenceEntries) ? userData.referenceEntries : (Array.isArray(userData.references) ? userData.references : []));
        
        const links = userData.professionalLinks || {};
        let linksObject = {};
        if (Array.isArray(links)) {
          links.forEach(link => {
            const type = link.type?.toLowerCase();
            if (type === 'linkedin') linksObject.linkedin = link.url;
            else if (type === 'github') linksObject.github = link.url;
            else if (type === 'portfolio') linksObject.portfolio = link.url;
            else if (type === 'personal website' || type === 'website') linksObject.website = link.url;
          });
        } else if (typeof links === 'object') {
          linksObject = links;
        }
        
        setProfessionalLinks({
          linkedin: linksObject.linkedin || linksObject.linkedinUrl || '',
          github: linksObject.github || linksObject.githubUrl || '',
          portfolio: linksObject.portfolio || linksObject.portfolioUrl || '',
          website: linksObject.website || linksObject.websiteUrl || ''
        });
        
        // Disable edit mode and all editing sections when viewing another user's profile
        setEditMode(false);
        setEditingSections({
          personal: false,
          residency: false,
          locations: false,
          professional: false,
          jobPreferences: false,
          memberships: false,
          additional: false,
          skills: false,
          references: false,
          experience: false,
          education: false,
          certifications: false,
          languages: false,
          socialLinks: false
        });
        
        // Use backend's profileCompletion if available, otherwise calculate locally
        const backendCompletion = userData.profileCompletion || userData.profileCompletionPercentage || userData.profileStatus?.completionPercentage;
        console.log('🔍 Profile Completion Check (Admin Viewing):');
        console.log('   - userData.profileCompletion:', userData.profileCompletion);
        console.log('   - userData.profileCompletionPercentage:', userData.profileCompletionPercentage);
        console.log('   - userData.profileStatus?.completionPercentage:', userData.profileStatus?.completionPercentage);
        console.log('   - Final backendCompletion:', backendCompletion);
        
        if (backendCompletion !== undefined && backendCompletion !== null) {
          console.log(`✅ Using BACKEND profileCompletion for viewed user: ${backendCompletion}%`);
          setProfileCompletionPercentage(backendCompletion);
        } else {
          console.log('⚠️ No backend profileCompletion for viewed user, will be calculated by useEffect');
          // Will be calculated by the useEffect hook that watches profileForm
        }
        
        setLoading(false);
        
        console.log('✅ Other user profile loaded successfully');
      } catch (error) {
        console.error('❌ Error loading other user profile:', error);
        setLoading(false);
      }
    };
    
    loadOtherUserProfile();
  }, [viewUserId, isViewingOtherProfile]);

  useEffect(() => {
    // Skip loading own profile if viewing another user
    if (isViewingOtherProfile) return;
    
    let isMounted = true; // Prevent state updates after component unmount

    // Add page visibility change listener to refresh data when user returns
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        console.log('🔄 Page became visible, refreshing data...');
        refreshDataAfterEdit();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const load = async () => {
      try {
        console.log('🔄 Loading profile data...');
        setLoading(true);
        
        // Check for cached data first (faster initial render)
        const cachedData = sessionStorage.getItem('myProfileData');
        const cacheTimestamp = sessionStorage.getItem('myProfileTimestamp');
        const cacheMaxAge = 5 * 60 * 1000; // 5 minutes
        
        // Use cached data if available and not expired
        if (cachedData && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < cacheMaxAge) {
          console.log('⚡ Loading from cache (fast!)');
          const p = JSON.parse(cachedData);
          
          if (!isMounted) return;
          
          // Populate form with cached data immediately
          setProfileForm(prev => ({
            ...prev,
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            middleName: p.middleName || '',
            email: p.email || '',
            phoneNumber: p.phone || p.phoneNumber || '',
            phone: p.phone || p.phoneNumber || '',
            altPhone: p.altPhone || '',
            dateOfBirth: p.dateOfBirth || '',
            gender: p.gender || '',
            bloodGroup: p.bloodGroup || '',
            ngo: p.ngo || '',
            hasDisability: p.hasDisability || 'no',
            disabilityDescription: p.disabilityDescription || '',
            community: p.community || '',
            nationality: p.nationality || '',
            residentCountry: p.residentCountry || '',
            currentCity: p.currentCity || '',
            postalCode: p.postalCode || '',
            address: p.address || '',
            latitude: p.latitude || '',
            longitude: p.longitude || '',
            workPermit: p.workPermit || '',
            preferredLocation1: p.preferredLocation1 || '',
            preferredLocation2: p.preferredLocation2 || '',
            preferredLocation3: p.preferredLocation3 || '',
            willingToRelocate: p.willingToRelocate || '',
            location: (() => {
              // Handle location - convert object to string if needed
              if (typeof p.location === 'string') {
                return p.location;
              } else if (p.location && typeof p.location === 'object') {
                const loc = p.location;
                return `${loc.city || loc.currentCity || ''}, ${loc.country || loc.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || 
                       loc.address || 
                       `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || '';
              } else {
                return `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
              }
            })(),
            professionalTitle: p.professionalTitle || '',
            professionalSummary: p.professionalSummary || p.summary || '',
            summary: p.summary || p.professionalSummary || '',
            yearsOfExperience: p.yearsOfExperience || p.yearsExperience || '',
            yearsExperience: p.yearsExperience || p.yearsOfExperience || '',
            expectedSalary: p.expectedSalary || '',
            jobTypePreference: p.jobTypePreference || '',
            availability: p.availability || '',
            industry: p.industry || '',
            careerLevel: p.careerLevel || '',
            noticePeriod: p.noticePeriod || '',
            currentSalary: p.currentSalary || '',
            currencyPreference: p.currencyPreference || '',
            travelAvailability: p.travelAvailability || '',
            workLocation: p.workLocation || '',
            preferredJobTitles: p.preferredJobTitles || '',
            membershipOrg: p.membershipOrg || '',
            membershipType: p.membershipType || '',
            membershipDate: p.membershipDate || '',
            careerObjectives: p.careerObjectives || '',
            hobbies: p.hobbies || '',
            additionalComments: p.additionalComments || '',
            agreeTerms: p.agreeTerms || false,
            allowContact: p.allowContact || false,
            jobType: p.jobType || '',
            skills: Array.isArray(p.coreSkills) ? p.coreSkills.join(', ') : (Array.isArray(p.skills) ? p.skills.join(', ') : (Array.isArray(p.technicalSkills) ? p.technicalSkills.join(', ') : (p.skills || ''))),
            tools: Array.isArray(p.tools) ? p.tools.join(', ') : (p.tools || ''),
            softSkills: Array.isArray(p.softSkills) ? p.softSkills.join(', ') : (p.softSkills || ''),
            profilePhoto: (() => {
              const photoPath = p.profilePhoto || p.profilePicture || p.profilePhotoPath || p.profileImage || null;
              if (!photoPath) return null;
              
              // Check if it's an old/broken path and skip it
              const pathStr = String(photoPath);
              if (pathStr.includes('jobseeker_profiles/photos')) {
                console.log('⚠️ Skipping old photo path in cached data:', pathStr);
                return null; // Return null to use photoPreview or initials
              }
              
              if (typeof photoPath === 'string' && (photoPath.startsWith('http://') || photoPath.startsWith('https://'))) {
                return photoPath;
              }
              
              // Normalize path separators (handle Windows backslashes)
              let normalizedPath = typeof photoPath === 'string' ? photoPath.replace(/\\/g, '/') : photoPath;
              
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace(/^uploads\//, '');
                return buildApiUrl(`/api/users/uploads/${uploadPath}`);
              }
              if (normalizedPath.startsWith('/')) {
                if (normalizedPath.startsWith('/uploads/')) {
                  const uploadPath = normalizedPath.replace(/^\/uploads\//, '');
                  return buildApiUrl(`/api/users/uploads/${uploadPath}`);
                }
                const baseUrl = buildApiUrl('');
                const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                return `${cleanBaseUrl}${normalizedPath}`;
              }
              
              // If it has path separators, assume it's in uploads
              if (normalizedPath.includes('/')) {
                return buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              }
              
              return photoPath;
            })()
          }));
          
          setEducation(Array.isArray(p.educationEntries) ? p.educationEntries : (Array.isArray(p.education) ? p.education : []));
          setExperience(Array.isArray(p.experienceEntries) ? p.experienceEntries : (Array.isArray(p.experience) ? p.experience : []));
          setLanguages(Array.isArray(p.languages) ? p.languages : []);
          setCertifications(Array.isArray(p.certificationEntries) ? p.certificationEntries : (Array.isArray(p.certifications) ? p.certifications : []));
          const referencesData = Array.isArray(p.referenceEntries) ? p.referenceEntries : (Array.isArray(p.references) ? p.references : []);
          setReferences(referencesData);
          console.log(`📞 Loading References from cache: ${referencesData.length} items`, referencesData);
          
          // Handle professional links - convert array format to object format
          const links = p.professionalLinks || {};
          let linksObject = {};
          if (Array.isArray(links)) {
            links.forEach(link => {
              const type = link.type?.toLowerCase();
              if (type === 'linkedin') linksObject.linkedin = link.url;
              else if (type === 'github') linksObject.github = link.url;
              else if (type === 'portfolio') linksObject.portfolio = link.url;
              else if (type === 'personal website' || type === 'website') linksObject.website = link.url;
            });
          } else if (typeof links === 'object') {
            linksObject = links;
          }
          
          setProfessionalLinks({
            linkedin: linksObject.linkedin || linksObject.linkedinUrl || '',
            github: linksObject.github || linksObject.githubUrl || '',
            portfolio: linksObject.portfolio || linksObject.portfolioUrl || '',
            website: linksObject.website || linksObject.websiteUrl || ''
          });
          
          // Use BACKEND's calculation for consistency
          const backendCompletion = p.profileCompletion || p.profileStatus?.completionPercentage || 0;
          const frontendCompletion = calculateProfileCompletion();
          console.log(`📊 Profile Completion from BACKEND (cache): ${backendCompletion}%`);
          console.log(`📊 Profile Completion from FRONTEND calc (cache): ${frontendCompletion}%`);
          console.log(`✅ Using BACKEND value for consistency`);
          setProfileCompletionPercentage(backendCompletion || frontendCompletion);
          
          setLoading(false);
          console.log('✅ Cached data loaded successfully');
          
          // Check cache age - only fetch fresh if cache is older than 5 minutes
          const cacheAge = Date.now() - parseInt(cacheTimestamp);
          const FIVE_MINUTES = 5 * 60 * 1000;
          
          if (cacheAge > FIVE_MINUTES) {
            console.log('🔄 Cache is old (>5 min), fetching fresh data in background...');
            fetchFreshData();
          } else {
            console.log('✅ Cache is fresh (<5 min), skipping background fetch to preserve recent edits');
          }
          return;
        }
        
        // No cache or expired - fetch fresh data
        await fetchFreshData();
        
      } catch (error) {
        if (!isMounted) return;
        console.error('❌ Error loading profile:', error);
        console.error('Error details:', error.response?.data || error.message);
        setLoading(false);
      }
    };
    
    const fetchFreshData = async () => {
      try {
        // Skip if viewing another user's profile
        if (isViewingOtherProfile) return;
        
        console.log('🔄 Fetching fresh data from /api/jobseeker/profile...');
        const res = await axios.get(buildApiUrl('/api/jobseeker/profile'), { headers: authHeaders() });
        const p = res?.data || {};
        
        console.log('\n' + '='.repeat(80));
        console.log('📊 RAW DATA RECEIVED FROM BACKEND');
        console.log('='.repeat(80));
        console.log('Full response:', p);
        console.log(`✅ Received ${Object.keys(p).filter(k => p[k] && p[k] !== '').length} filled fields out of ${Object.keys(p).length} total fields`);
        console.log('\n📋 Field-by-field breakdown:');
        Object.keys(p).forEach(key => {
          if (p[key] && p[key] !== '' && !['_id', 'userId', 'createdAt', 'updatedAt'].includes(key)) {
            const value_preview = typeof p[key] === 'string' && p[key].length > 50 ? p[key].substring(0, 50) + '...' : JSON.stringify(p[key]);
            console.log(`  ✓ ${key}: ${value_preview}`);
          }
        });
        console.log('='.repeat(80) + '\n');
        
        if (!isMounted) return; // Don't update state if component unmounted
        
        // Cache the fresh data
        sessionStorage.setItem('myProfileData', JSON.stringify(p));
        sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
        console.log('💾 Profile data cached for faster loading')
        
        setProfileForm(prev => ({
          ...prev,
          // Basic Information - Map all possible field names
          firstName: user?.firstName || p.firstName || '',
          lastName: user?.lastName || p.lastName || '',
          middleName: p.middleName || '',
          email: user?.email || p.email || localStorage.getItem('userEmail') || '',
          phoneNumber: p.phoneNumber || p.phone || '',
          phone: p.phone || p.phoneNumber || '',
          altPhone: p.altPhone || '',
          
          // Personal Details
          dateOfBirth: p.dateOfBirth || '',
          gender: p.gender || '',
          bloodGroup: p.bloodGroup || '',
          community: p.community || p.primary_community || '',
          
          // Location & Address
          nationality: p.nationality || '',
          residentCountry: p.residentCountry || p.location?.country || '',
          currentCity: p.currentCity || p.location?.city || '',
          postalCode: p.postalCode || '',
          address: p.address || '',
          latitude: p.latitude || p.location?.coordinates?.[1] || '',
          longitude: p.longitude || p.location?.coordinates?.[0] || '',
          workPermit: p.workPermit || '',
          preferredLocation1: p.preferredLocation1 || '',
          preferredLocation2: p.preferredLocation2 || '',
          preferredLocation3: p.preferredLocation3 || '',
          willingToRelocate: p.willingToRelocate || '',
          location: (() => {
            // Handle location - convert object to string if needed
            if (typeof p.location === 'string') {
              return p.location;
            } else if (p.location && typeof p.location === 'object') {
              const loc = p.location;
              return `${loc.city || loc.currentCity || ''}, ${loc.country || loc.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || 
                     loc.address || 
                     `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || '';
            } else {
              return `${p.currentCity || ''}, ${p.residentCountry || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
            }
          })(),
          professionalTitle: p.professionalTitle || '',
          professionalSummary: p.professionalSummary || p.summary || '',
          summary: p.summary || p.professionalSummary || '',
          yearsOfExperience: p.yearsOfExperience || p.yearsExperience || '',
          yearsExperience: p.yearsExperience || p.yearsOfExperience || '',
          careerLevel: p.careerLevel || '',
          industry: p.industry || '',
          preferredJobTitles: p.preferredJobTitles || '',
          jobType: p.jobType || '',
          jobTypePreference: p.jobTypePreference || p.job_type || '',
          noticePeriod: p.noticePeriod || '',
          currentSalary: p.currentSalary || '',
          expectedSalary: p.expectedSalary || '',
          currencyPreference: p.currencyPreference || p.currency || '',
          travelAvailability: p.travelAvailability || '',
          workLocation: p.workLocation || '',
          availability: typeof p.availability === 'string' ? p.availability : (Array.isArray(p.availability) ? p.availability.join(', ') : ''),
          membershipOrg: p.membershipOrg || '',
          membershipType: p.membershipType || '',
          membershipDate: p.membershipDate || '',
          careerObjectives: p.careerObjectives || '',
          hobbies: p.hobbies || '',
          additionalComments: p.additionalComments || '',
          participateInVolunteering: p.participateInVolunteering || '',
          volunteeringDetails: p.volunteeringDetails || '',
          agreeTerms: p.agreeTerms || false,
          allowContact: p.allowContact || false,
          skills: Array.isArray(p.coreSkills) ? p.coreSkills.join(', ') : (Array.isArray(p.skills) ? p.skills.join(', ') : (p.skills || '')),
          tools: Array.isArray(p.tools) ? p.tools.join(', ') : (p.tools || ''),
          profilePhoto: (() => {
            const photoPath = p.profilePhoto || p.profilePicture || p.profilePhotoPath || p.profileImage || null;
            if (!photoPath) return null;
            
            // Check if it's an old/broken path and skip it
            const pathStr = String(photoPath);
            if (pathStr.includes('jobseeker_profiles/photos')) {
              console.log('⚠️ Skipping old photo path:', pathStr);
              return null; // Return null to use photoPreview or initials
            }
            if (typeof photoPath === 'string' && (photoPath.startsWith('http://') || photoPath.startsWith('https://'))) {
              return photoPath;
            }
            
            // Normalize path separators (handle Windows backslashes)
            let normalizedPath = typeof photoPath === 'string' ? photoPath.replace(/\\/g, '/') : photoPath;
            
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace(/^uploads\//, '');
              return buildApiUrl(`/api/users/uploads/${uploadPath}`);
            }
            if (normalizedPath.startsWith('/')) {
              if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace(/^\/uploads\//, '');
                return buildApiUrl(`/api/users/uploads/${uploadPath}`);
              }
              const baseUrl = buildApiUrl('');
              const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
              return `${cleanBaseUrl}${normalizedPath}`;
            }
            
            // If it has path separators, assume it's in uploads
            if (normalizedPath.includes('/')) {
              return buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            
            return photoPath;
          })()
        }));
        
        setEducation(Array.isArray(p.educationEntries) ? p.educationEntries : (Array.isArray(p.education) ? p.education : []));
        setExperience(Array.isArray(p.experienceEntries) ? p.experienceEntries : (Array.isArray(p.experience) ? p.experience : (Array.isArray(p.workExperience) ? p.workExperience : [])));
        setLanguages(Array.isArray(p.languages) ? p.languages.map(l => (typeof l === 'string' ? { language: l, proficiency: '' } : l)) : []);
        setCertifications(Array.isArray(p.certificationEntries) ? p.certificationEntries : (Array.isArray(p.certifications) ? p.certifications : []));
        const links = p.professionalLinks || {};
        console.log('🔗 Loading Professional Links - Raw data:', links, 'Type:', Array.isArray(links) ? 'Array' : typeof links);
        let linksObject = {};
        
        if (Array.isArray(links)) {
          // Convert array format to object format
          console.log('🔗 Converting array format to object format');
          links.forEach(link => {
            const type = (link.type || '').toLowerCase();
            console.log(`🔗 Processing link: type="${type}", url="${link.url}"`);
            if (type === 'linkedin') linksObject.linkedin = link.url;
            else if (type === 'github') linksObject.github = link.url;
            else if (type === 'portfolio') linksObject.portfolio = link.url;
            else if (type === 'personal website' || type === 'website') linksObject.website = link.url;
          });
        } else if (typeof links === 'object') {
          // Already in object format
          console.log('🔗 Using object format directly');
          linksObject = links;
        }
        
        console.log('🔗 Final linksObject:', linksObject);
        setProfessionalLinks({
          linkedin: linksObject.linkedin || linksObject.linkedinUrl || '',
          github: linksObject.github || linksObject.githubUrl || '',
          portfolio: linksObject.portfolio || linksObject.portfolioUrl || '',
          website: linksObject.website || linksObject.websiteUrl || ''
        });
        console.log('🔗 Set professionalLinks state:', {
          linkedin: linksObject.linkedin || linksObject.linkedinUrl || '',
          github: linksObject.github || linksObject.githubUrl || '',
          portfolio: linksObject.portfolio || linksObject.portfolioUrl || '',
          website: linksObject.website || linksObject.websiteUrl || ''
        });
        const freshReferencesData = Array.isArray(p.referenceEntries) ? p.referenceEntries : (Array.isArray(p.references) ? p.references : []);
        setReferences(freshReferencesData);
        console.log(`📞 Loading References from fresh data: ${freshReferencesData.length} items`, freshReferencesData);
        
        console.log('\n' + '='.repeat(80));
        console.log('✅ PROFILE DATA SUCCESSFULLY LOADED AND STATE UPDATED');
        console.log('='.repeat(80));
        console.log('📊 ProfileForm state (after mapping):');
        console.log(`  First Name: ${p.firstName || 'EMPTY'}`);
        console.log(`  Middle Name: ${p.middleName || 'EMPTY'}`);
        console.log(`  Last Name: ${p.lastName || 'EMPTY'}`);
        console.log(`  Email: ${p.email || 'EMPTY'}`);
        console.log(`  Phone: ${p.phone || 'EMPTY'}`);
        console.log(`  Alt Phone: ${p.altPhone || 'EMPTY'}`);
        console.log(`  Date of Birth: ${p.dateOfBirth || 'EMPTY'}`);
        console.log(`  Gender: ${p.gender || 'EMPTY'}`);
        console.log(`  Blood Group: ${p.bloodGroup || 'EMPTY'}`);
        console.log(`  Communities/Associations: ${p.communitiesAssociations?.length || 0} items`);
        console.log(`  Nationality: ${p.nationality || 'EMPTY'}`);
        console.log(`  Resident Country: ${p.residentCountry || 'EMPTY'}`);
        console.log(`  Current City: ${p.currentCity || 'EMPTY'}`);
        console.log(`  Work Permit: ${p.workPermit || 'EMPTY'}`);
        console.log(`  Professional Title: ${p.professionalTitle || 'EMPTY'}`);
        console.log(`  Years Experience: ${p.yearsExperience || p.yearsOfExperience || 'EMPTY'}`);
        console.log(`  Career Level: ${p.careerLevel || 'EMPTY'}`);
        console.log(`  Industry: ${p.industry || 'EMPTY'}`);
        console.log('📋 Array fields:');
        console.log(`  Education Entries: ${Array.isArray(p.educationEntries) ? p.educationEntries.length : 0} items`);
        console.log(`  Experience Entries: ${Array.isArray(p.experienceEntries) ? p.experienceEntries.length : 0} items`);
        console.log(`  Languages: ${Array.isArray(p.languages) ? p.languages.length : 0} items`);
        console.log(`  Certifications: ${Array.isArray(p.certificationEntries) ? p.certificationEntries.length : 0} items`);
        console.log(`  References: ${Array.isArray(p.referenceEntries) ? p.referenceEntries.length : (Array.isArray(p.references) ? p.references.length : 0)} items`);
        console.log(`  Core Skills: ${Array.isArray(p.coreSkills) ? p.coreSkills.length : 0} items`);
        console.log(`  Tools: ${Array.isArray(p.tools) ? p.tools.length : 0} items`);
        if (Array.isArray(p.referenceEntries) || Array.isArray(p.references)) {
          const refs = p.referenceEntries || p.references;
          console.log(`  📞 References Details:`, refs);
        }
        console.log('='.repeat(80) + '\n');
        
        // Use BACKEND's calculation for consistency with Dashboard
        const backendCompletion = p.profileCompletion || p.profileStatus?.completionPercentage || 0;
        const frontendCompletion = calculateProfileCompletion();
        console.log(`📊 Profile Completion from BACKEND: ${backendCompletion}%`);
        console.log(`📊 Profile Completion from FRONTEND calc: ${frontendCompletion}%`);
        console.log(`✅ Using BACKEND value for consistency with Dashboard`);
        setProfileCompletionPercentage(backendCompletion || frontendCompletion);
        
        setLoading(false);
      } catch (error) {
        if (!isMounted) return;
        console.error('❌ Error loading profile:', error);
        console.error('Error details:', error.response?.data || error.message);
        setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false; // Cleanup function
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const saveProfile = async () => {
    try {
      setSaving(true);
      console.log('💾 Saving entire profile...');
      
      const payload = {
        // Basic Information - Map all possible field names
        firstName: profileForm.firstName || '',
        middleName: profileForm.middleName || '',
        lastName: profileForm.lastName || '',
        fullName: `${profileForm.firstName || ''} ${profileForm.middleName || ''} ${profileForm.lastName || ''}`.trim(),
        email: profileForm.email || '',
        phone: profileForm.phone || profileForm.phoneNumber || '',
        phoneNumber: profileForm.phone || profileForm.phoneNumber || '',
        altPhone: profileForm.altPhone || '',
        
        // Personal Details
        dateOfBirth: profileForm.dateOfBirth || '',
        gender: profileForm.gender || '',
        bloodGroup: profileForm.bloodGroup || '',
        communitiesAssociations: profileForm.communitiesAssociations || [],
        
        // Location & Address
        nationality: profileForm.nationality || '',
        residentCountry: profileForm.residentCountry || '',
        currentCity: profileForm.currentCity || '',
        postalCode: profileForm.postalCode || '',
        address: profileForm.address || '',
        latitude: profileForm.latitude || '',
        longitude: profileForm.longitude || '',
        workPermit: profileForm.workPermit || '',
        
        // Preferred Working Locations
        preferredLocation1: profileForm.preferredLocation1 || '',
        preferredLocation2: profileForm.preferredLocation2 || '',
        preferredLocation3: profileForm.preferredLocation3 || '',
        willingToRelocate: profileForm.willingToRelocate || '',
        workLocation: profileForm.workLocation || '',
        
        // Professional Profile
        professionalTitle: profileForm.professionalTitle || '',
        professionalSummary: profileForm.professionalSummary || profileForm.summary || '',
        summary: profileForm.professionalSummary || profileForm.summary || '',
        yearsExperience: profileForm.yearsExperience || profileForm.yearsOfExperience || '',
        yearsOfExperience: profileForm.yearsExperience || profileForm.yearsOfExperience || '',
        careerLevel: profileForm.careerLevel || '',
        industry: profileForm.industry || '',
        
        // Job Preferences
        preferredJobTitles: profileForm.preferredJobTitles || '',
        jobType: profileForm.jobType || profileForm.jobTypePreference || '',
        jobTypePreference: profileForm.jobType || profileForm.jobTypePreference || '',
        noticePeriod: profileForm.noticePeriod || '',
        currentSalary: profileForm.currentSalary || '',
        expectedSalary: profileForm.expectedSalary || '',
        currencyPreference: profileForm.currencyPreference || '',
        travelAvailability: profileForm.travelAvailability || '',
        availability: profileForm.availability || '',
        
        // Professional Memberships
        membershipOrg: profileForm.membershipOrg || '',
        membershipType: profileForm.membershipType || '',
        membershipDate: profileForm.membershipDate || '',
        
        // Additional Information
        careerObjectives: profileForm.careerObjectives || '',
        hobbies: profileForm.hobbies || '',
        additionalComments: profileForm.additionalComments || '',
        participateInVolunteering: profileForm.participateInVolunteering || '',
        volunteeringDetails: profileForm.volunteeringDetails || '',
        agreeTerms: profileForm.agreeTerms || false,
        allowContact: profileForm.allowContact || false,
        
        // Skills & Tools - Convert to arrays
        skills: profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        coreSkills: profileForm.skills ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        tools: profileForm.tools ? profileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        softwareTools: profileForm.tools ? profileForm.tools.split(',').map(s => s.trim()).filter(Boolean) : [],
        
        // Array Fields - Use both naming conventions for compatibility
        education: education,
        educationEntries: education,
        experience: experience,
        experienceEntries: experience,
        workExperience: experience,
        languages: languages,
        languageProficiency: languages,
        certifications: certifications,
        certificationEntries: certifications,
        references: references,
        referenceEntries: references,
        professionalLinks: professionalLinks,
        
        // Profile completion status
        profileCompleted: true,
        hasCompletedProfile: true
      };
      
      // Ensure array fields are properly included
      const finalPayload = ensureArrayFieldsSaved(payload);
      
      console.log('📤 Sending profile payload:', finalPayload);
      console.log('🩸 Blood Group in payload:', payload.bloodGroup);
      console.log('👤 Demographics fields in payload:', {
        bloodGroup: payload.bloodGroup,
        dateOfBirth: payload.dateOfBirth,
        gender: payload.gender,
        communitiesAssociations: payload.communitiesAssociations,
        nationality: payload.nationality,
        currentCity: payload.currentCity
      });
      console.log('📋 Array fields in payload:', {
        education: payload.education?.length || 0,
        experience: payload.experience?.length || 0,
        languages: payload.languages?.length || 0,
        certifications: payload.certifications?.length || 0,
        references: payload.references?.length || 0,
        skills: payload.skills?.length || 0,
        tools: payload.tools?.length || 0
      });
      console.log('🔗 Professional links in payload:', finalPayload.professionalLinks);
      
      // No validation - allow saving with any fields filled or empty
      console.log('💾 Saving profile without validation checks...');
      
      // If there's a photo file, use FormData; otherwise use JSON
      let response;
      if (selectedPhotoFile) {
        console.log('📸 Uploading profile with photo...');
        const formData = new FormData();
        formData.append('profilePhoto', selectedPhotoFile);
        
        // Append all profile data as JSON string (backend will parse it)
        formData.append('profileData', JSON.stringify(finalPayload));
        
        // Also append each field individually for backward compatibility
        Object.keys(finalPayload).forEach(key => {
          const value = finalPayload[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              // For objects, stringify them
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              // For arrays, stringify them
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        response = await axios.put(buildApiUrl('/api/user/profile'), formData, {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Regular JSON save
        response = await axios.put(buildApiUrl('/api/user/profile'), finalPayload, { 
          headers: { 
            ...authHeaders(), 
            'Content-Type': 'application/json' 
          } 
        });
      }
      
      console.log('✅ Profile saved successfully:', response.data);
      
      // Update profileForm with the new photo path if returned
      if (selectedPhotoFile && (response.data.profileImage || response.data.profilePhotoPath)) {
        const photoPath = response.data.profileImage || response.data.profilePhotoPath;
        console.log('📸 New photo path from server:', photoPath);
        if (photoPath && typeof photoPath === 'string') {
          let normalizedPath = photoPath.replace(/\\/g, '/');
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace('uploads/', '');
            const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            // Also update photoPreview to the new URL so it displays immediately
            setPhotoPreview(fullUrl);
          } else if (normalizedPath.startsWith('/uploads/')) {
            const uploadPath = normalizedPath.replace('/uploads/', '');
            const fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            setPhotoPreview(fullUrl);
          } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            console.log('📸 Setting profilePhoto to full URL:', normalizedPath);
            setProfileForm(p => ({ ...p, profilePhoto: normalizedPath }));
            setPhotoPreview(normalizedPath);
          } else {
            // Assume it's a relative path
            const fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            console.log('📸 Setting profilePhoto to:', fullUrl);
            setProfileForm(p => ({ ...p, profilePhoto: fullUrl }));
            setPhotoPreview(fullUrl);
          }
        }
      }
      
      setEditMode(false);
      
      // Update cache with the saved data instead of clearing it
      const updatedCache = {
        ...JSON.parse(sessionStorage.getItem('myProfileData') || '{}'),
        ...finalPayload
      };
      if (selectedPhotoFile && response.data.profileImage) {
        updatedCache.profileImage = response.data.profileImage;
        updatedCache.profilePhotoPath = response.data.profileImage;
      }
      sessionStorage.setItem('myProfileData', JSON.stringify(updatedCache));
      sessionStorage.setItem('myProfileTimestamp', Date.now().toString());
      
      alert('Profile saved successfully!');
      
      // Keep photoPreview to ensure new photo displays
      // Only clear the file selection, keep the preview URL
      setSelectedPhotoFile(null);
      
      // Refresh data to ensure persistence
      // Don't clear photoPreview - let it persist so the new photo displays
      setTimeout(async () => {
        await refreshDataAfterEdit();
        // After refresh, if we have a new photoPreview URL, keep it
        // The refresh will load the new photo path from backend
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error saving profile:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Error saving profile: ${error.response?.data?.error || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Full screen styling with proper viewport handling
  const containerStyle = { 
    padding: '30px 30px 60px 30px', 
    maxWidth: '100%', 
    width: '100%', 
    margin: '0',
    minHeight: '100vh',
    boxSizing: 'border-box'
  };
  const elevatedCardStyle = { 
    border: '1px solid #edf2f7', 
    borderRadius: '16px', 
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)', 
    background: '#fff', 
    marginBottom: '20px',
    width: '100%',
    maxWidth: '100%'
  };
  const profileHeaderStyle = { 
    background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)', 
    color: '#fff', 
    padding: '12px 24px', 
    borderRadius: '16px', 
    marginBottom: '10px', 
    minHeight: '90px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%'
  };

  const headerPatternStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cdefs%3E%3Cpattern id=\'grain\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'1\' fill=\'white\' opacity=\'0.1\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grain)\'/%3E%3C/svg%3E")',
    opacity: 0.3,
    zIndex: 1
  };
  const profileAvatarStyle = { 
    width: '76px', 
    height: '76px', 
    borderRadius: '50%', 
    background: 'rgba(255,255,255,0.25)',
    backdropFilter: 'blur(10px)',
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    fontSize: '30px', 
    fontWeight: 700, 
    color: '#ffffff', 
    border: '3px solid rgba(255,255,255,0.4)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    textShadow: '0 4px 8px rgba(0,0,0,0.5)',
    position: 'relative',
    overflow: 'hidden'
  };
  const gridStyle = { 
    display: 'grid', 
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
    gap: '20px', 
    marginBottom: '20px',
    width: '100%'
  };
  const formInputBase = { 
    width: '100%', 
    padding: '10px 15px', 
    border: '2px solid #e0e0e0', 
    borderRadius: '8px', 
    fontSize: '14px', 
    transition: 'all 0.3s',
    boxSizing: 'border-box'
  };
  const viewModeField = editMode ? {} : { background: '#f7f9fc', borderColor: '#e6eaf1', color: '#333' };
  const pillTagStyle = { 
    display: 'inline-block', 
    background: '#f8fafc', 
    color: '#1e293b', 
    padding: '8px 14px', 
    borderRadius: '999px', 
    margin: '5px', 
    fontSize: '14px', 
    border: '2px solid #e2e8f0',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const handleVerifyReference = async (reference, index) => {
    if (isViewingOtherProfile) return;

    const email = (reference?.referenceEmail || reference?.contact || '').trim().toLowerCase();
    if (!email) {
      alert('Please provide an email for this reference before requesting verification.');
      return;
    }

    const name = (reference?.referenceName || reference?.name || '').trim();
    if (!name) {
      alert('Please provide a name for this reference before requesting verification.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again to verify references.');
      return;
    }

    const position = (reference?.referenceJobTitle || reference?.jobTitle || '').trim();
    const company = (reference?.referenceCompany || reference?.company || '').trim();
    const relationship = (reference?.referenceRelationship || reference?.relation || 'Professional').trim() || 'Professional';
    const phone = (reference?.referencePhone || profileForm.phone || profileForm.phoneNumber || 'Not provided').toString();
    const yearsKnown = Number(reference?.yearsKnown || reference?.referenceYearsKnown || reference?.years || 1) || 1;

    const payload = {
      name,
      email,
      phone,
      company: company || 'Not provided',
      position: position || 'Not provided',
      relationship,
      yearsKnown
    };

    setVerifyingReferenceIndex(index);
    try {
      const response = await fetch(buildApiUrl('/api/references/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok) {
        alert('Verification email has been sent to your reference.');
        setReferenceStatusMap(prev => ({
          ...prev,
          [email]: {
            status: 'pending',
            verifiedAt: null,
            updatedAt: new Date().toISOString()
          }
        }));
        await fetchReferenceStatuses();
      } else {
        const message = responseData?.error || responseData?.message || 'Failed to send verification email.';
        alert(`❌ ${message}`);
      }
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      alert('❌ Something went wrong while sending the verification email.');
    } finally {
      setVerifyingReferenceIndex(null);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div style={{ 
        padding: '30px', 
        maxWidth: '1200px', 
        margin: '0 auto',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        minHeight: '100vh'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '12px',
          padding: '30px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            height: '40px', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'loading 1.5s ease-in-out infinite',
            borderRadius: '8px',
            marginBottom: '20px',
            width: '60%'
          }} />
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ marginBottom: '20px' }}>
              <div style={{ 
                height: '20px', 
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'loading 1.5s ease-in-out infinite',
                borderRadius: '4px',
                marginBottom: '10px',
                width: '40%'
              }} />
              <div style={{ 
                height: '40px', 
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                backgroundSize: '200% 100%',
                animation: 'loading 1.5s ease-in-out infinite',
                borderRadius: '4px',
                marginBottom: '10px'
              }} />
            </div>
          ))}
        </div>
        <style>{`
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  const sidebarWidth = 280;
  const sidebarStyles = {
    width: isMobileView ? `${MOBILE_SIDEBAR_WIDTH}px` : `${sidebarWidth}px`,
    maxWidth: isMobileView ? '72vw' : undefined,
    minWidth: isMobileView ? 0 : undefined,
    flexShrink: 0,
    background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
    padding: isMobileView ? '1rem 0.85rem' : '2rem 1.5rem',
    boxShadow: isMobileView ? '4px 0 20px rgba(15, 23, 42, 0.2)' : '4px 0 20px rgba(249, 115, 22, 0.15)',
    position: 'fixed',
    left: 0,
    top: isMobileView ? MOBILE_HEADER_HEIGHT : 0,
    height: isMobileView ? `calc(100vh - ${MOBILE_HEADER_HEIGHT}px)` : '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: isMobileView ? 1150 : 1000,
    color: '#ffffff',
    transform: isMobileView ? (mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    transition: 'transform 0.28s ease',
    pointerEvents: isMobileView && !mobileSidebarOpen ? 'none' : 'auto',
    boxSizing: 'border-box'
  };

  const mainContentStyles = {
    marginLeft: isMobileView ? 0 : `${sidebarWidth}px`,
    width: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    maxWidth: isMobileView ? '100%' : `calc(100% - ${sidebarWidth}px)`,
    overflowX: 'hidden',
    overflowY: 'auto',
    minHeight: isMobileView ? 'auto' : '100vh',
    boxSizing: 'border-box',
    backgroundColor: '#f8fafc',
    paddingTop: 0,
    flex: '1 1 auto',
    minWidth: 0
  };

  const responsiveContainerStyle = {
    ...containerStyle,
    padding: isMobileView ? '12px 12px 72px 12px' : containerStyle.padding,
    maxWidth: '100%',
    width: '100%',
    overflowX: 'hidden'
  };

  const handleSidebarNavClick = (e) => {
    if (isMobileView && e.target.closest('button')) {
      setTimeout(() => setMobileSidebarOpen(false), 200);
    }
  };

  return (
    <>
      <style>{`
        .sidebar-container, .sidebar-container * {
          color: #ffffff !important;
        }
        .sidebar-container button, .sidebar-container button * {
          color: #ffffff !important;
        }
        .sidebar-container span, .sidebar-container span * {
          color: #ffffff !important;
        }
        .sidebar-container p, .sidebar-container p * {
          color: #ffffff !important;
        }
        .sidebar-container h1, .sidebar-container h2, .sidebar-container h3, .sidebar-container h4, .sidebar-container h5, .sidebar-container h6 {
          color: #ffffff !important;
        }
        .sidebar-container div, .sidebar-container div * {
          color: #ffffff !important;
        }
        .sidebar-container emoji, .sidebar-container emoji * {
          color: #ffffff !important;
        }
        .profile-header h2, .profile-header p, .profile-header span {
          color: #ffffff !important;
        }
        
        /* Fix for Cancel button visibility - More specific selectors */
        button.cancel-btn-fixed,
        .cancel-btn-fixed,
        button.btn-secondary.cancel-btn-fixed {
          background: #dc3545 !important;
          color: #ffffff !important;
          border: 2px solid #dc3545 !important;
          padding: 8px 16px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          font-weight: 600 !important;
          transition: all 0.3s ease !important;
          opacity: 1 !important;
          visibility: visible !important;
          display: inline-block !important;
        }
        
        button.cancel-btn-fixed:hover,
        .cancel-btn-fixed:hover,
        button.btn-secondary.cancel-btn-fixed:hover {
          background: #c82333 !important;
          border-color: #c82333 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3) !important;
        }
        
        /* Override any conflicting styles */
        .my-profile-page button.cancel-btn-fixed,
        .my-profile-page .cancel-btn-fixed {
          background: #dc3545 !important;
          color: #ffffff !important;
          border: 2px solid #dc3545 !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Orange-Teal theme for Edit buttons */
        .my-profile-page button.edit-btn,
        .my-profile-page .edit-btn {
          background: linear-gradient(135deg, #f97316 0%, #0d9488 100%) !important;
          color: #ffffff !important;
          border: 2px solid #f97316 !important;
          padding: 8px 16px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          font-weight: 600 !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 2px 8px rgba(249, 115, 22, 0.3) !important;
        }
        
        .my-profile-page button.edit-btn:hover,
        .my-profile-page .edit-btn:hover {
          background: linear-gradient(135deg, #ea580c 0%, #0f766e 100%) !important;
          border-color: #ea580c !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4) !important;
        }
      `}</style>
      <div
        className={`my-profile-page${isMobileView && mobileSidebarOpen ? ' js-profile-drawer-open' : ''}`}
        style={{ 
        display: 'flex', 
        flexDirection: isMobileView ? 'column' : 'row',
        minHeight: '100vh', 
        width: '100%',
        backgroundColor: '#f8fafc',
        overflowX: 'hidden'
      }}>
      {isMobileView && (
        <header className="js-profile-mobile-header">
          <div className="js-profile-header-start">
            <button
              type="button"
              className="js-profile-menu-btn btn-icon"
              onClick={() => setMobileSidebarOpen((prev) => !prev)}
              aria-label={mobileSidebarOpen ? 'Close profile navigation' : 'Open profile navigation'}
            >
              <FontAwesomeIcon icon={mobileSidebarOpen ? faTimes : faBars} />
            </button>
          </div>
          <h1 className="js-profile-mobile-title">
            {isViewingOtherProfile ? 'Profile' : 'My Profile'}
          </h1>
          <div className="js-profile-header-end">
            <button
              type="button"
              className="js-profile-back-btn"
              onClick={() => (isViewingOtherProfile ? navigate(-1) : navigate('/jobseeker-dashboard'))}
            >
              {isViewingOtherProfile ? 'Back' : 'Dashboard'}
            </button>
          </div>
        </header>
      )}
      {isMobileView && mobileSidebarOpen && (
        <div
          className="js-profile-drawer-overlay"
          style={{
            top: MOBILE_HEADER_HEIGHT,
            left: `min(${MOBILE_SIDEBAR_WIDTH}px, 72vw)`
          }}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <div
        style={sidebarStyles}
        className="sidebar-container js-profile-drawer"
        onClick={handleSidebarNavClick}
      >
        {!isMobileView && (
        <div className="js-profile-drawer-intro" style={{ marginBottom: '2rem' }}>
          <h2 style={{ 
            color: '#ffffff !important', 
            fontSize: '1.5rem', 
            fontWeight: '800', 
            marginBottom: '0.5rem',
            textShadow: '0 4px 8px rgba(0,0,0,0.7)',
            letterSpacing: '0.5px'
          }}>
            <span style={{ color: '#ffffff !important' }}>My Profile</span>
          </h2>
          <p style={{ 
            color: '#ffffff !important', 
            fontSize: '0.9rem',
            margin: 0,
            fontWeight: '700',
            textShadow: '0 2px 4px rgba(0,0,0,0.6)'
          }}>
            <span style={{ color: '#ffffff !important' }}>Manage your professional information</span>
          </p>
        </div>
        )}
        
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            color: '#ffffff !important', 
            fontSize: '1.1rem', 
            fontWeight: '700', 
            marginBottom: '1rem',
            textShadow: '0 3px 6px rgba(0,0,0,0.7)',
            letterSpacing: '0.3px'
          }}>
            <span style={{ color: '#ffffff !important' }}>Quick Actions</span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>📋 Personal Information</span>
            </button>
            <button 
              onClick={() => {
                const element = document.querySelector('[data-section="professional"]');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>💼 Professional Details</span>
            </button>
            <button 
              onClick={() => {
                const element = document.querySelector('[data-section="skills"]');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>🛠️ Skills & Expertise</span>
            </button>
            <button 
              onClick={() => {
                const element = document.querySelector('[data-section="demographics"]');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>👤 Demographics</span>
            </button>
            <button 
              onClick={() => {
                const element = document.querySelector('[data-section="residency"]');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>🌍 Residency</span>
            </button>
            <button 
              onClick={() => {
                const element = document.querySelector('[data-section="locations"]');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '2px solid rgba(255,255,255,0.4)',
                color: '#ffffff !important',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.5)';
                e.target.style.transform = 'translateX(4px)';
                e.target.style.borderColor = 'rgba(255,255,255,0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.3)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
            >
              <span style={{ color: '#ffffff !important' }}>📍 Work Locations</span>
            </button>
            {!isViewingOtherProfile && (viewerRole === 'job_seeker' || viewerRole === 'jobseeker') && (
              <>
              <button
                onClick={() => setShowResumeUpload(true)}
                style={{
                  background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.5) 0%, rgba(34, 197, 94, 0.4) 100%)',
                  border: '2px solid rgba(255,255,255,0.5)',
                  color: '#ffffff !important',
                  padding: '0.75rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(249, 115, 22, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
              >
                <span style={{ color: '#ffffff !important' }}>📄 Upload / Update Resume</span>
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.6rem', width: '100%', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.6)',
                    color: '#ffffff !important',
                    padding: '0.65rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                  onClick={() => {
                    sessionStorage.setItem('myProfileData', JSON.stringify(profileForm || {}));
                    navigate('/smart-resume');
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                  }}
                >
                  <span style={{ color: '#ffffff !important', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>👁 Preview</span>
                </button>
                <button
                  type="button"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #f97316, #22c55e)',
                    border: '2px solid rgba(255,255,255,0.6)',
                    color: '#ffffff !important',
                    padding: '0.65rem 0.75rem',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)'
                  }}
                  onClick={async () => {
                    try {
                      const res = await fetch(buildApiUrl('/api/jobseeker/resume'), {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                          Accept: 'application/pdf',
                          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                        },
                      });

                      if (!res.ok) {
                        console.error('Download resume failed', await res.text());
                        alert('Could not download resume. Please try again.');
                        return;
                      }

                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = resolveDownloadFilename(res, profileForm, 'Resume.pdf');
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (e) {
                      console.error('Download resume error', e);
                      alert('Could not download resume. Please try again.');
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.9)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                  }}
                >
                  <span style={{ color: '#ffffff !important', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🚀 Download PDF</span>
                </button>
              </div>
              </>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ 
            color: '#ffffff !important', 
            fontSize: '1.1rem', 
            fontWeight: '700', 
            marginBottom: '1rem',
            textShadow: '0 3px 6px rgba(0,0,0,0.7)',
            letterSpacing: '0.3px'
          }}>
            <span style={{ color: '#ffffff !important' }}>Profile Stats</span>
          </h3>
          <div style={{ 
            background: 'rgba(0,0,0,0.4)', 
            padding: '1rem', 
            borderRadius: '8px',
            border: '2px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: '#ffffff !important', fontSize: '0.9rem', fontWeight: '700', textShadow: '0 2px 4px rgba(0,0,0,0.7)' }}>Completion</span>
              <span style={{ color: '#ffffff !important', fontWeight: '800', fontSize: '1rem', textShadow: '0 2px 4px rgba(0,0,0,0.7)' }}>{profileCompletionPercentage}%</span>
            </div>
            <div style={{ 
              background: 'rgba(0,0,0,0.3)', 
              height: '8px', 
              borderRadius: '4px',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
            <div style={{ 
              background: 'linear-gradient(90deg, #ffffff 0%, #f0f0f0 100%)', 
              height: '100%', 
              width: `${profileCompletionPercentage}%`,
              borderRadius: '4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'width 0.5s ease'
            }}></div>
            </div>
            {!isViewingOtherProfile && (viewerRole === 'job_seeker' || viewerRole === 'jobseeker') && (
              <a
                href="/jobseeker-registration"
                onClick={(e) => { e.preventDefault(); navigate('/jobseeker-registration'); }}
                style={{
                  display: 'inline-block',
                  marginTop: '0.75rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  textDecoration: 'none',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.35)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Complete Profile →
              </a>
            )}
          </div>
        </div>

        {!(isViewingOtherProfile && ['employer', 'admin', 'super_admin', 'superadmin', 'administrator'].includes(viewerRole)) && (
          <div>
            <h3 style={{ 
              color: '#ffffff !important', 
              fontSize: '1.1rem', 
              fontWeight: '700', 
              marginBottom: '1rem',
              textShadow: '0 3px 6px rgba(0,0,0,0.7)',
              letterSpacing: '0.3px'
            }}>
              <span style={{ color: '#ffffff !important' }}>Tips</span>
            </h3>
            <div style={{ 
              background: 'rgba(0,0,0,0.4)', 
              padding: '1rem', 
              borderRadius: '8px',
              border: '2px solid rgba(255,255,255,0.3)',
              boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)'
            }}>
              <p style={{ 
                color: '#ffffff', 
                fontSize: '0.85rem', 
                margin: 0,
                lineHeight: '1.4',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.7)'
              }}>
                <span style={{ color: '#ffffff !important', textShadow: '0 2px 4px rgba(0,0,0,0.7)' }}>💡 Keep your profile updated to get better job matches</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content" style={mainContentStyles}>
      <div style={responsiveContainerStyle}>
        <div style={profileHeaderStyle} className="profile-header">
          <div style={headerPatternStyle}></div>
          
          {/* Role-aware back button for external profile viewers */}
          {isViewingOtherProfile && (
            <button
              onClick={() => {
                if (viewerRole === 'employer') {
                  navigate('/employer-dashboard');
                } else if (viewerRole === 'recruiter') {
                  navigate('/recruiter-dashboard');
                } else {
                  navigate('/admin?view=profile-browser');
                }
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '30px',
                zIndex: 10,
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {viewerRole === 'employer' || viewerRole === 'recruiter'
                ? 'Back to Dashboard'
                : 'Back to Profile Browser'}
            </button>
          )}
          
          <div className="profile-header-body" style={{ display: 'flex', alignItems: 'center', gap: '30px', position: 'relative', zIndex: 2 }}>
            <div style={{ position: 'relative' }}>
              <div style={profileAvatarStyle}>
                {(() => {
                  // Priority: photoPreview > profilePhoto > initials
                  if (photoPreview && typeof photoPreview === 'string') {
                    return (
                      <img 
                        src={photoPreview} 
                        alt={`${profileForm.firstName} ${profileForm.lastName}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        onError={(e) => {
                          console.error('❌ Error loading photoPreview:', photoPreview);
                          e.target.style.display = 'none';
                        }}
                      />
                    );
                  }
                  if (profileForm.profilePhoto) {
                    // Check if it's an old/broken path
                    const isOldPath = profileForm.profilePhoto.includes('jobseeker_profiles/photos');
                    return (
                      <img 
                        src={profileForm.profilePhoto} 
                        alt={`${profileForm.firstName} ${profileForm.lastName}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        onError={(e) => {
                          console.error('❌ Error loading profilePhoto:', profileForm.profilePhoto);
                          e.target.style.display = 'none';
                          // Clear old/broken photo path from state
                          if (isOldPath || !profileForm.profilePhoto.startsWith('http')) {
                            console.log('🔄 Removing old/broken photo path from state');
                            setProfileForm(p => ({ ...p, profilePhoto: null }));
                          }
                        }}
                      />
                    );
                  }
                  return null;
                })()}
                {(!photoPreview && !profileForm.profilePhoto) && (
                  <>{(profileForm.firstName?.[0] || 'U')}{(profileForm.lastName?.[0] || '')}</>
                )}
              </div>
              {!isViewingOtherProfile && (
                <label
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    background: '#f97316',
                    color: 'white',
                    border: '3px solid white',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    fontSize: '14px',
                    transition: 'all 0.2s ease',
                    zIndex: 10
                  }}
                  title="Change Photo"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ea580c';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f97316';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <FontAwesomeIcon icon={faCamera} />
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                </label>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <h2 style={{ 
                  marginBottom: '0', 
                  fontSize: '32px', 
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  color: '#ffffff'
                }}>{profileForm.firstName} {profileForm.lastName}</h2>
                {hasAssociationVerification && (
                  <CommunityMemberBadge
                    type="association"
                    size="small"
                    label={associationBadgeLabel}
                  />
                )}
              </div>
              <p style={{ 
                fontSize: '16px',
                fontWeight: '500',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                color: '#ffffff',
                marginTop: '2px',
                marginBottom: 0
              }}>
                {(() => {
                  const locationDisplay = getPrimaryLocationDisplay();
                  const lat = profileForm.latitude ? parseFloat(profileForm.latitude) : null;
                  const lng = profileForm.longitude ? parseFloat(profileForm.longitude) : null;
                  let mapsUrl = 'https://www.google.com/maps/search/?api=1';

                  if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== null && lng !== null) {
                    mapsUrl += `&query=${encodeURIComponent(`${lat},${lng}`)}`;
                  } else if (locationDisplay && locationDisplay !== '—') {
                    mapsUrl += `&query=${encodeURIComponent(locationDisplay)}`;
                  } else {
                    mapsUrl = null;
                  }

                  if (mapsUrl) {
                    return (
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View location on Google Maps"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          color: '#ffffff',
                          textDecoration: 'none',
                          background: 'rgba(255,255,255,0.15)',
                          padding: '4px 12px',
                          borderRadius: '999px',
                          transition: 'background 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                        }}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>{locationDisplay}</span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '13px' }} />
                      </a>
                    );
                  }

                  return (
                    <>
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> {locationDisplay}
                    </>
                  );
                })()}
              </p>
            </div>
            {!isViewingOtherProfile && (!isMobileView || mobileSidebarOpen) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                  onClick={() => navigate('/jobseeker-dashboard')}
                  style={{
                    background: '#f97316',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#ea580c';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#f97316';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="personal">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faUser} /> Personal Information</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.personal ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => toggleSectionEdit('personal')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success"
                    onClick={() => saveSection('personal')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => toggleSectionEdit('personal')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>

          {/* Row 1: First Name, Middle Name, Last Name */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.firstName || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, firstName: e.target.value }))} 
                placeholder="Enter your first name"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Middle Name</label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.middleName || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, middleName: e.target.value }))} 
                placeholder="Enter your middle name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.lastName || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, lastName: e.target.value }))} 
                placeholder="Enter your last name"
                required
              />
            </div>
          </div>

          {/* Row 2: Date of Birth, Gender, Blood Group */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Date of Birth <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="date" 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.dateOfBirth || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, dateOfBirth: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Gender <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.gender || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, gender: e.target.value }))}
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Blood Group</label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.bloodGroup || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, bloodGroup: e.target.value }))}
              >
                <option value="">Select blood group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>

          {/* Row 3: Email, Phone, Alt Phone */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
          <div className="form-group">
              <label className="form-label">Email Address <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="email" 
                style={{ ...formInputBase, ...viewModeField, backgroundColor: '#f3f4f6', cursor: 'not-allowed' }} 
                value={profileForm.email || ''} 
                disabled
                placeholder="your.email@example.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="tel" 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.phone || profileForm.phoneNumber || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value, phoneNumber: e.target.value }))} 
                placeholder="+254 700 000 000"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Alternative Phone</label>
              <input 
                type="tel" 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.altPhone || ''} 
                disabled={!editingSections.personal} 
                onChange={(e) => setProfileForm(p => ({ ...p, altPhone: e.target.value }))} 
                placeholder="+1 234 567 8900"
              />
          </div>
        </div>

          {/* Row 4: Community (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label">Community / Association</label>
              {editingSections.personal ? (
                <CommunityAssociationSelector
                  value={profileForm.communitiesAssociations || []}
                  onChange={(items) => setProfileForm(p => ({ ...p, communitiesAssociations: items }))}
                />
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.communitiesAssociations && profileForm.communitiesAssociations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {profileForm.communitiesAssociations.map((item, index) => (
                        <div key={index} style={{
                          padding: '8px 12px',
                          background: 'white',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <strong>{item.name}</strong>
                            <span style={{
                              marginLeft: '10px',
                              padding: '2px 8px',
                              background: item.type === 'association' ? '#e3f2fd' : '#f3e5f5',
                              color: item.type === 'association' ? '#1976d2' : '#7b1fa2',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {item.type === 'association' ? 'Association' : 'Community'}
                            </span>
                            {item.country && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                                • {item.country}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No communities/associations added</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 5: NGO (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group" style={{ width: '100%', overflow: 'visible' }}>
              <label className="form-label">NGO</label>
              {editingSections.personal ? (
                <NGOSelector
                  value={profileForm.ngo || ''}
                  onChange={(ngo) => setProfileForm(p => ({ ...p, ngo }))}
                />
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.ngo ? (
                    <span style={{ fontWeight: '500' }}>{profileForm.ngo}</span>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No NGO selected</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row 6: Disability (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group" style={{ width: '100%' }}>
              <label className="form-label">Do you have any disability?</label>
              {editingSections.personal ? (
                <>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '8px', marginBottom: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                      <input
                        type="radio"
                        name="hasDisability"
                        value="no"
                        checked={profileForm.hasDisability === 'no'}
                        onChange={(e) => setProfileForm(p => ({ ...p, hasDisability: e.target.value, disabilityDescription: e.target.value === 'no' ? '' : p.disabilityDescription }))}
                        style={{ marginRight: '8px' }}
                      />
                      No
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                      <input
                        type="radio"
                        name="hasDisability"
                        value="yes"
                        checked={profileForm.hasDisability === 'yes'}
                        onChange={(e) => setProfileForm(p => ({ ...p, hasDisability: e.target.value }))}
                        style={{ marginRight: '8px' }}
                      />
                      Yes
                    </label>
                  </div>
                  {profileForm.hasDisability === 'yes' && (
                    <textarea
                      name="disabilityDescription"
                      placeholder="Please describe"
                      value={profileForm.disabilityDescription || ''}
                      onChange={(e) => setProfileForm(p => ({ ...p, disabilityDescription: e.target.value }))}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        marginTop: '8px',
                        resize: 'vertical'
                      }}
                    />
                  )}
                </>
              ) : (
                <div style={{ padding: '12px', background: '#f9f9f9', borderRadius: '6px', minHeight: '40px' }}>
                  {profileForm.hasDisability === 'yes' ? (
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '8px' }}>Yes</div>
                      {profileForm.disabilityDescription && (
                        <div style={{ color: '#666', fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                          {profileForm.disabilityDescription}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Nationality & Residency Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="residency">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faMapMarkerAlt} /> Nationality & Residency</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.residency ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => toggleSectionEdit('residency')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button 
                      className="btn btn-success"
                      onClick={() => saveSection('residency')} 
                      disabled={saving}
                    >
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => toggleSectionEdit('residency')}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Row 1: Nationality, Resident Country */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Nationality <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.nationality || ''} 
                disabled={!editingSections.residency} 
                onChange={(e) => setProfileForm(p => ({ ...p, nationality: e.target.value }))}
                placeholder="Select your nationality"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Resident Country <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.residentCountry || ''} 
                disabled={!editingSections.residency} 
                onChange={(e) => setProfileForm(p => ({ ...p, residentCountry: e.target.value }))}
                placeholder="Select your current country of residence"
                required
              />
            </div>
          </div>

          {/* Row 2: Current City, Postal Code */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Current City <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.currentCity || ''} 
                disabled={!editingSections.residency} 
                onChange={(e) => setProfileForm(p => ({ ...p, currentCity: e.target.value }))}
                placeholder="e.g., Nairobi"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Postal/ZIP Code</label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.postalCode || ''} 
                disabled={!editingSections.residency} 
                onChange={(e) => setProfileForm(p => ({ ...p, postalCode: e.target.value }))}
                placeholder="Enter postal code"
              />
            </div>
          </div>

          {/* Row 3: Full Address */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Full Address</label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.address || ''} 
                disabled={!editingSections.residency} 
                onChange={(e) => setProfileForm(p => ({ ...p, address: e.target.value }))}
                placeholder="Street address, building, apartment number"
              />
          </div>
        </div>

          {/* Location Preview Button */}
          <div style={{ marginBottom: '20px' }}>
            <button
              type="button"
              onClick={openLocationDialog}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: '#f97316',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#ea580c';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f97316';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(249, 115, 22, 0.3)';
              }}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {profileForm.latitude && profileForm.longitude 
                ? 'Edit Location & View Map' 
                : 'Set Location & View Map'}
            </button>
          </div>

          {/* Row 5: Work Permit */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Do you have a valid work permit for your resident country? <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                value={profileForm.workPermit || ''}
                disabled={!editingSections.residency}
                onChange={(e) => setProfileForm(p => ({ ...p, workPermit: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  cursor: editingSections.residency ? 'pointer' : 'not-allowed',
                  backgroundColor: editingSections.residency ? '#fff' : '#f5f5f5'
                }}
                onFocus={(e) => {
                  if (editingSections.residency) {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="citizen">Citizen</option>
                <option value="not_required">Not Required</option>
              </select>
            </div>
          </div>
        </div>

        {/* Preferred Working Locations Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="locations">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faMapMarkerAlt} /> Preferred Working Locations</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.locations ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => toggleSectionEdit('locations')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <>
                    <button 
                      className="btn btn-success"
                      onClick={() => saveSection('locations')} 
                      disabled={saving}
                    >
                      <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => toggleSectionEdit('locations')}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Info Badge */}
          <div style={{ marginBottom: '20px' }}>
            <label className="form-label">Select up to 3 countries where you'd prefer to work <span style={{ color: '#dc2626' }}>*</span></label>
            <div style={{ padding: '12px', background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '13px', color: '#0369a1', marginTop: '8px' }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
              This helps match you with opportunities in your preferred locations
            </div>
          </div>

          {/* Row 1: Preferred Locations 1, 2, 3 */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Preferred Location 1 <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                value={profileForm.preferredLocation1 || ''}
                disabled={!editingSections.locations}
                onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation1: e.target.value }))}
                required
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  cursor: editingSections.locations ? 'pointer' : 'not-allowed',
                  backgroundColor: editingSections.locations ? '#fff' : '#f5f5f5',
                  ...viewModeField
                }}
                onFocus={(e) => {
                  if (editingSections.locations) {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Preferred Location 2</label>
              <select
                value={profileForm.preferredLocation2 || ''}
                disabled={!editingSections.locations}
                onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation2: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  cursor: editingSections.locations ? 'pointer' : 'not-allowed',
                  backgroundColor: editingSections.locations ? '#fff' : '#f5f5f5',
                  ...viewModeField
                }}
                onFocus={(e) => {
                  if (editingSections.locations) {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Preferred Location 3</label>
              <select
                value={profileForm.preferredLocation3 || ''}
                disabled={!editingSections.locations}
                onChange={(e) => setProfileForm(p => ({ ...p, preferredLocation3: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  cursor: editingSections.locations ? 'pointer' : 'not-allowed',
                  backgroundColor: editingSections.locations ? '#fff' : '#f5f5f5',
                  ...viewModeField
                }}
                onFocus={(e) => {
                  if (editingSections.locations) {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select country</option>
                {COMMUNITY_COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Willing to Relocate */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Willing to Relocate Internationally? <span style={{ color: '#dc2626' }}>*</span></label>
              <select
                value={profileForm.willingToRelocate || ''}
                disabled={!editingSections.locations}
                onChange={(e) => setProfileForm(p => ({ ...p, willingToRelocate: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  boxSizing: 'border-box',
                  cursor: editingSections.locations ? 'pointer' : 'not-allowed',
                  backgroundColor: editingSections.locations ? '#fff' : '#f5f5f5'
                }}
                onFocus={(e) => {
                  if (editingSections.locations) {
                    e.target.style.borderColor = '#f97316';
                    e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="">Select an option</option>
                <option value="yes">Yes, immediately</option>
                <option value="conditional">Yes, with conditions</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          {/* Row 3: Work Location Preference */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Work Location Preference <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.workLocation || ''} 
                disabled={!editingSections.locations} 
                onChange={(e) => setProfileForm(p => ({ ...p, workLocation: e.target.value }))}
                required
              >
                <option value="">Select preference</option>
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
                <option value="flexible">Flexible/Open to all</option>
              </select>
            </div>
          </div>
        </div>

        {/* Professional Profile Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="professional">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faBriefcase} /> Professional Profile</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.professional ? (
                  <button 
                    className="btn btn-primary" 
                    onClick={() => toggleSectionEdit('professional')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => saveSection('professional')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => toggleSectionEdit('professional')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {/* Row 1: Professional Title (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Professional Title/Headline <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.professionalTitle || ''} 
                disabled={!editingSections.professional} 
                onChange={(e) => setProfileForm(p => ({ ...p, professionalTitle: e.target.value }))} 
                placeholder="e.g., Senior Software Engineer | Full Stack Developer"
                required
              />
            </div>
          </div>

          {/* Row 2: Years of Experience, Career Level */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Total Years of Experience <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.yearsExperience || profileForm.yearsOfExperience || ''} 
                disabled={!editingSections.professional} 
                onChange={(e) => setProfileForm(p => ({ ...p, yearsExperience: e.target.value, yearsOfExperience: e.target.value }))}
                required
              >
                <option value="">Select experience</option>
                <option value="0-1">0-1 years (Entry Level)</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5-7">5-7 years</option>
                <option value="7-10">7-10 years</option>
                <option value="10-15">10-15 years</option>
                <option value="15+">15+ years</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Career Level <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.careerLevel || ''} 
                disabled={!editingSections.professional} 
                onChange={(e) => setProfileForm(p => ({ ...p, careerLevel: e.target.value }))}
                required
              >
                <option value="">Select level</option>
                <option value="entry">Entry Level</option>
                <option value="mid">Mid-Level</option>
                <option value="senior">Senior Level</option>
                <option value="lead">Lead/Principal</option>
                <option value="manager">Manager</option>
                <option value="director">Director</option>
                <option value="executive">Executive/C-Level</option>
              </select>
            </div>
          </div>

          {/* Row 3: Industry (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Industry/Sector <span style={{ color: '#dc2626' }}>*</span></label>
              <input 
                type="text"
                list="profile-industry-options"
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.industry || ''} 
                disabled={!editingSections.professional} 
                onChange={(e) => setProfileForm(p => ({ ...p, industry: e.target.value }))}
                placeholder="Select from dropdown or type your industry"
                autoComplete="off"
                required
              />
              <datalist id="profile-industry-options">
                <option value="Technology & IT" />
                <option value="Finance & Banking" />
                <option value="Healthcare & Medical" />
                <option value="Education & Training" />
                <option value="Manufacturing" />
                <option value="Retail & E-commerce" />
                <option value="Hospitality & Tourism" />
                <option value="Construction & Real Estate" />
                <option value="Agriculture & Agribusiness" />
                <option value="Energy & Utilities" />
                <option value="Telecommunications" />
                <option value="Media & Entertainment" />
                <option value="Consulting & Professional Services" />
                <option value="FMCGs and CPGs" />
                <option value="Government & Public Sector" />
                <option value="NGO & Non-Profit" />
                <option value="Legal Services" />
                <option value="Marketing & Advertising" />
                <option value="Logistics & Transportation" />
                <option value="Pharmaceutical & Biotech" />
                <option value="Automotive" />
                <option value="Aerospace & Defense" />
                <option value="Insurance" />
                <option value="Real Estate" />
                <option value="Food & Beverage" />
                <option value="Fashion & Apparel" />
                <option value="Sports & Fitness" />
              </datalist>
              {editingSections.professional && (
                <small style={{ display: 'block', marginTop: '5px', color: '#666', fontSize: '0.85em' }}>
                  💡 Tip: Select from dropdown or type your own industry
                </small>
              )}
            </div>
          </div>

          {/* Row 4: Professional Summary (full width) */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Professional Summary <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea 
                style={{ 
                  ...formInputBase, 
                  minHeight: '120px', 
                  maxHeight: '300px',
                  resize: 'vertical', 
                  ...viewModeField,
                  width: '100%',
                  boxSizing: 'border-box'
                }} 
                value={profileForm.professionalSummary || profileForm.summary || ''} 
                disabled={!editingSections.professional} 
                onChange={(e) => setProfileForm(p => ({ ...p, professionalSummary: e.target.value, summary: e.target.value }))} 
                placeholder="Write a compelling summary about your professional background, key achievements, and career goals. Highlight what makes you unique and valuable to potential employers globally. (Minimum 150 characters)"
                required
              />
            </div>
          </div>
        </div>

        {/* Job Preferences & Availability Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="jobPreferences">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faBriefcase} /> Job Preferences & Availability</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.jobPreferences ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => toggleSectionEdit('jobPreferences')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success"
                    onClick={() => saveSection('jobPreferences')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => toggleSectionEdit('jobPreferences')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>

          {/* Preferred Job Titles (Full width) */}
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label className="form-label">Preferred Job Titles</label>
            <input 
              type="text" 
              style={{ ...formInputBase, ...viewModeField }} 
              value={profileForm.preferredJobTitles || ''} 
              disabled={!editingSections.jobPreferences} 
              onChange={(e) => setProfileForm(p => ({ ...p, preferredJobTitles: e.target.value }))}
              placeholder="e.g., Software Engineer, Full Stack Developer, Backend Engineer"
            />
            <small style={{ color: '#666', fontSize: '13px', marginTop: '5px', display: 'block' }}>
              Enter job titles you're interested in, separated by commas
            </small>
              </div>

          {/* Row 1: Job Type, Notice Period */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Desired Job Type <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.jobType || profileForm.jobTypePreference || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, jobType: e.target.value, jobTypePreference: e.target.value }))}
                required
              >
                <option value="">Select job type</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="freelance">Freelance</option>
                <option value="internship">Internship</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Notice Period <span style={{ color: '#dc2626' }}>*</span></label>
              <select 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.noticePeriod || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, noticePeriod: e.target.value }))}
                required
              >
                <option value="">Select notice period</option>
                <option value="immediate">Immediate/Available Now</option>
                <option value="1-week">1 Week</option>
                <option value="2-weeks">2 Weeks</option>
                <option value="1-month">1 Month</option>
                <option value="2-months">2 Months</option>
                <option value="3-months">3 Months</option>
                <option value="more">More than 3 Months</option>
              </select>
            </div>
          </div>

          {/* Row 2: Current Salary, Expected Salary */}
          <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Current Salary (Optional)</label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.currentSalary || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, currentSalary: e.target.value }))}
                placeholder="e.g., USD 50,000 per year"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Salary</label>
              <input 
                type="text"
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.expectedSalary || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, expectedSalary: e.target.value }))}
                placeholder="e.g., USD 60,000 - 70,000 per year"
              />
            </div>
          </div>

          {/* Row 3: Currency Preference */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Currency Preference</label>
              <select 
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.currencyPreference || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, currencyPreference: e.target.value }))}
              >
                <option value="">Select currency</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="KES">KES - Kenyan Shilling</option>
                <option value="ZAR">ZAR - South African Rand</option>
                <option value="NGN">NGN - Nigerian Naira</option>
                <option value="AED">AED - UAE Dirham</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Row 4: Travel Availability */}
          <div style={{ marginBottom: '15px' }}>
            <div className="form-group">
              <label className="form-label">Travel Availability</label>
              <select 
                style={{ ...formInputBase, ...viewModeField, width: '100%' }} 
                value={profileForm.travelAvailability || ''} 
                disabled={!editingSections.jobPreferences} 
                onChange={(e) => setProfileForm(p => ({ ...p, travelAvailability: e.target.value }))}
              >
                <option value="">Select availability</option>
                <option value="no-travel">No Travel</option>
                <option value="minimal">Minimal (Less than 25%)</option>
                <option value="moderate">Moderate (25-50%)</option>
                <option value="frequent">Frequent (50-75%)</option>
                <option value="extensive">Extensive (More than 75%)</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="memberships">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faBuilding} /> Professional Memberships & Associations</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.memberships ? (
                  <button 
                    className="btn btn-primary" 
                    onClick={() => toggleSectionEdit('memberships')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => saveSection('memberships')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => toggleSectionEdit('memberships')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          <div style={gridStyle}>
            <div className="form-group">
              <label className="form-label">Organization Name</label>
              <input style={{ ...formInputBase, ...viewModeField }} value={profileForm.membershipOrg || ''} disabled={!editingSections.memberships} onChange={(e) => setProfileForm(p => ({ ...p, membershipOrg: e.target.value }))} placeholder="e.g., IEEE, ACM, PMI" />
            </div>
            <div className="form-group">
              <label className="form-label">Membership Type</label>
              <input style={{ ...formInputBase, ...viewModeField }} value={profileForm.membershipType || ''} disabled={!editingSections.memberships} onChange={(e) => setProfileForm(p => ({ ...p, membershipType: e.target.value }))} placeholder="e.g., Student, Professional, Fellow" />
            </div>
            <div className="form-group">
              <label className="form-label">Membership Since</label>
              <input 
                type="month" 
                style={{ ...formInputBase, ...viewModeField }} 
                value={profileForm.membershipDate || ''} 
                disabled={!editingSections.memberships} 
                onChange={(e) => setProfileForm(p => ({ ...p, membershipDate: e.target.value }))} 
              />
            </div>
          </div>
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="additional">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faUser} /> Additional Information</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.additional ? (
                  <button 
                    className="btn btn-primary" 
                    onClick={() => toggleSectionEdit('additional')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => saveSection('additional')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => toggleSectionEdit('additional')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label className="form-label">Career Objectives</label>
            <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '80px' }} value={profileForm.careerObjectives || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, careerObjectives: e.target.value }))} placeholder="What are your short-term and long-term career goals? What do you hope to achieve in your next role?" />
          </div>
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label className="form-label">Hobbies & Interests</label>
            <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '80px' }} value={profileForm.hobbies || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, hobbies: e.target.value }))} placeholder="Tell us about your interests and hobbies" />
          </div>
          <div className="form-group">
            <label className="form-label">Additional Comments</label>
            <textarea style={{ ...formInputBase, ...viewModeField, minHeight: '100px' }} value={profileForm.additionalComments || ''} disabled={!editingSections.additional} onChange={(e) => setProfileForm(p => ({ ...p, additionalComments: e.target.value }))} placeholder="Any additional information you'd like to share" />
          </div>
          
          <div className="form-group" style={{ marginBottom: '15px' }}>
            <label className="form-label">Would you like to participate in any volunteering?</label>
            <select 
              style={{ ...formInputBase, ...viewModeField }} 
              value={profileForm.participateInVolunteering || ''} 
              disabled={!editingSections.additional} 
              onChange={(e) => setProfileForm(p => ({ ...p, participateInVolunteering: e.target.value }))}
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {profileForm.participateInVolunteering === 'yes' && (
            <div className="form-group">
              <label className="form-label">Volunteering Details</label>
              <textarea 
                style={{ ...formInputBase, ...viewModeField, minHeight: '100px' }} 
                value={profileForm.volunteeringDetails || ''} 
                disabled={!editingSections.additional} 
                onChange={(e) => setProfileForm(p => ({ ...p, volunteeringDetails: e.target.value }))} 
                placeholder="Please describe the volunteering activities you'd like to participate in"
              />
            </div>
          )}
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }} data-section="skills">
          <style>{`
            [data-section="skills"] .card-header {
              border-bottom: 1px solid #3b82f6 !important;
              background: transparent !important;
              padding: 0 0 15px 0 !important;
            }
            [data-section="skills"] .card-title {
              background: none !important;
              -webkit-background-clip: unset !important;
              -webkit-text-fill-color: unset !important;
              color: #333 !important;
            }
            [data-section="skills"] .card-title svg {
              color: #60a5fa !important;
            }
            [data-section="skills"] .skills-input-container-comprehensive {
              display: flex !important;
              gap: 8px !important;
              margin-bottom: 10px !important;
              align-items: center !important;
            }
            [data-section="skills"] .skills-input-container-comprehensive input {
              flex: 1 !important;
              min-width: 0 !important;
            }
            [data-section="skills"] .add-btn-comprehensive,
            [data-section="skills"] button.add-btn-comprehensive {
              background: #f97316 !important;
              background-image: none !important;
              border: none !important;
              color: white !important;
              box-shadow: none !important;
              padding: 10px 16px !important;
              border-radius: 8px !important;
              font-weight: 600 !important;
              font-size: 14px !important;
              border-color: transparent !important;
              flex-shrink: 0 !important;
              white-space: nowrap !important;
              display: flex !important;
              align-items: center !important;
              gap: 4px !important;
              cursor: pointer !important;
              transition: all 0.2s ease !important;
            }
            [data-section="skills"] .add-btn-comprehensive:hover,
            [data-section="skills"] button.add-btn-comprehensive:hover {
              background: #ea580c !important;
              background-image: none !important;
              border: none !important;
              transform: none !important;
              border-color: transparent !important;
              box-shadow: none !important;
            }
            [data-section="skills"] .skills-input-container-comprehensive input {
              padding: 10px 15px !important;
              border: 2px solid #e5e7eb !important;
              border-left: 3px solid #f97316 !important;
              border-radius: 8px !important;
              font-size: 14px !important;
              outline: none !important;
              background-color: #ffffff !important;
            }
          `}</style>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.25rem', fontWeight: '700', margin: '0' }}>
              <FontAwesomeIcon icon={faLightbulb} style={{ color: '#60a5fa' }} /> 
              <span>Skills & Competencies</span>
            </h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.skills ? (
                  <button 
                    className="btn btn-primary" 
                    onClick={() => toggleSectionEdit('skills')}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => saveSection('skills')} 
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => toggleSectionEdit('skills')}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {/* Core Skills Subsection */}
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ marginBottom: '10px', color: '#333', fontSize: '16px', fontWeight: '600' }}>
              Core Skills <span style={{ color: '#dc2626' }}>*</span>
            </h4>
            
            {/* Info Bubble */}
            {editingSections.skills && (
              <div style={{ 
                padding: '12px 16px', 
                background: '#eff6ff', 
                border: '1px solid #bfdbfe', 
                borderRadius: '8px', 
                fontSize: '14px', 
                color: '#475569',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#3b82f6', fontSize: '16px' }} />
                <span>Add skills relevant to your profession and job preferences. Start typing to see suggestions.</span>
              </div>
            )}

            {editingSections.skills ? (
              <div>
                {/* Input + Add Button Side by Side */}
                <div className="skills-input-container-comprehensive skills-dropdown-container" style={{ display: 'flex', gap: '8px', marginBottom: '10px', position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="e.g., Python, Microsoft Excel, AutoCAD"
                    value={skillInputValue}
                    onChange={(e) => setSkillInputValue(e.target.value)}
                    onFocus={() => setSkillsDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && skillInputValue.trim()) {
                        e.preventDefault();
                        addSkill(skillInputValue);
                      }
                    }}
                    style={{
                      ...formInputBase,
                      flex: 1,
                      padding: '10px 15px',
                      border: '2px solid #e5e7eb',
                      borderLeft: '3px solid #f97316',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: '#ffffff',
                      minWidth: 0
                    }}
                  />
                  {skillsDropdownOpen && (
                    <div className="skills-dropdown-container" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '2px solid #f97316',
                      borderRadius: '8px',
                      boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}>
                      {commonSkills
                        .filter(skill => skill.toLowerCase().includes(skillInputValue.toLowerCase()))
                        .map((skill, index) => (
                          <div
                            key={index}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addSkill(skill);
                            }}
                            style={{
                              padding: '10px 15px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '14px',
                              color: '#333'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.background = 'white'}
                          >
                            {skill}
                          </div>
                        ))}
                      {skillInputValue.trim() && !commonSkills.some(s => s.toLowerCase() === skillInputValue.toLowerCase()) && (
                        <div
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addSkill(skillInputValue);
                          }}
                          style={{
                            padding: '10px 15px',
                            cursor: 'pointer',
                            borderTop: '2px solid #f97316',
                            fontSize: '14px',
                            color: '#f97316',
                            fontWeight: '600',
                            background: '#fff7ed'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#ffedd5'}
                          onMouseLeave={(e) => e.target.style.background = '#fff7ed'}
                        >
                          + Add "{skillInputValue}"
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Add Button */}
                  <button
                    type="button"
                    className="add-btn-comprehensive"
                    onClick={() => {
                      if (skillInputValue.trim()) {
                        addSkill(skillInputValue.trim());
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
                
                {/* Skills Tags Display */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                  {(profileForm.skills || '').split(',').filter(Boolean).map((s, i) => (
                    <div 
                      key={i}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: '1px solid #f59e0b',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fde68a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      }}
                    >
                      {s.trim()}
                      <span 
                        onClick={() => removeSkill(s.trim())}
                        style={{
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#92400e',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#92400e';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#92400e';
                        }}
                        title="Remove skill"
                      >×</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <div>
              {(profileForm.skills || '').split(',').filter(Boolean).map((s, i) => (
                  <span key={i} style={{
                    ...pillTagStyle,
                    background: '#fef3c7',
                    color: '#92400e',
                    border: '2px solid #f59e0b',
                    fontWeight: '700'
                  }}>{s.trim()}</span>
                ))}
                {(!profileForm.skills || profileForm.skills.split(',').filter(Boolean).length === 0) && (
                  <p style={{ color: '#999', fontStyle: 'italic' }}>No skills added yet</p>
                )}
            </div>
            )}
          </div>
          {/* Software & Tools Subsection */}
          <div>
            <h4 style={{ marginBottom: '10px', color: '#333', fontSize: '16px', fontWeight: '600' }}>
              Software & Tools
            </h4>
            
            {editingSections.skills ? (
              <div>
                {/* Dropdown + Add Button Side by Side */}
                <div className="skills-input-container-comprehensive" style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <select
                    value={selectedSoftwareTool}
                    onChange={(e) => setSelectedSoftwareTool(e.target.value)}
                    style={{
                      ...formInputBase,
                      flex: 1,
                      padding: '10px 15px',
                      border: '2px solid #e5e7eb',
                      borderLeft: '3px solid #f97316',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: '#ffffff',
                      minWidth: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select software or tool</option>
                    {softwareAndTools.map(tool => (
                      <option key={tool} value={tool}>{tool}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-btn-comprehensive"
                    onClick={() => {
                      const tool = selectedSoftwareTool;
                      if (tool) {
                        const currentTools = profileForm.tools ? profileForm.tools.split(',').map(t => t.trim()).filter(Boolean) : [];
                        if (!currentTools.includes(tool)) {
                          setProfileForm(prev => ({
                            ...prev,
                            tools: [...currentTools, tool].join(', ')
                          }));
                          setSelectedSoftwareTool('');
                        }
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </button>
                </div>
                
                {/* Tools Tags Display */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                  {(profileForm.tools || '').split(',').filter(Boolean).map((s, i) => (
                    <div 
                      key={i}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#d1fae5',
                        color: '#065f46',
                        borderRadius: '16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: '1px solid #10b981',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#a7f3d0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#d1fae5';
                      }}
                    >
                      {s.trim()}
                      <span 
                        onClick={() => {
                          const currentTools = profileForm.tools ? profileForm.tools.split(',').map(t => t.trim()).filter(Boolean) : [];
                          setProfileForm(prev => ({
                            ...prev,
                            tools: currentTools.filter(tool => tool !== s.trim()).join(', ')
                          }));
                        }}
                        style={{
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#065f46',
                          lineHeight: '1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#065f46';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#065f46';
                        }}
                        title="Remove tool"
                      >×</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <div>
              {(profileForm.tools || '').split(',').filter(Boolean).map((s, i) => (
                <span key={i} style={{
                  ...pillTagStyle,
                  background: '#d1fae5',
                  color: '#065f46',
                  border: '2px solid #10b981',
                  fontWeight: '700'
                }}>{s.trim()}</span>
              ))}
              {(!profileForm.tools || profileForm.tools.split(',').filter(Boolean).length === 0) && (
                <p style={{ color: '#999', fontStyle: 'italic' }}>No tools added yet</p>
              )}
            </div>
            )}
          </div>
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faUser} /> Professional References</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.references ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      console.log('🖊️ Edit clicked for References section');
                      setEditingSections(prev => {
                        console.log('📊 Previous editingSections:', prev);
                        const newState = { ...prev, references: true };
                        console.log('📊 New editingSections:', newState);
                        return newState;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('references');
                      setEditingSections(prev => ({ ...prev, references: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => setEditingSections(prev => ({ ...prev, references: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {(references || []).map((rf, idx) => (
            <div key={idx} style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', marginBottom: '12px', position: 'relative' }}>
              {editingSections.references ? (
                <div style={{ display: 'grid', gap: '15px', paddingRight: '40px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Reference Name</label>
                      <input
                        type="text"
                        value={rf.referenceName || rf.name || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referenceName: e.target.value, name: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Job Title</label>
                      <input
                        type="text"
                        value={rf.referenceJobTitle || rf.jobTitle || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referenceJobTitle: e.target.value, jobTitle: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="Position"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Company/Organization</label>
                      <input
                        type="text"
                        value={rf.referenceCompany || rf.company || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referenceCompany: e.target.value, company: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Relationship</label>
                      <input
                        type="text"
                        value={rf.referenceRelationship || rf.relation || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referenceRelationship: e.target.value, relation: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="e.g., Former Manager, Colleague"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Email</label>
                      <input
                        type="email"
                        value={rf.referenceEmail || rf.contact || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referenceEmail: e.target.value, contact: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Phone</label>
                      <input
                        type="tel"
                        value={rf.referencePhone || ''}
                        onChange={(e) => {
                          const newRef = [...references];
                          newRef[idx] = { ...newRef[idx], referencePhone: e.target.value };
                          setReferences(newRef);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                  </div>
                </div>
              ) : (() => {
                const emailKey = (rf.referenceEmail || rf.contact || '').trim().toLowerCase();
                const statusInfo = referenceStatusMap[emailKey] || {};
                const hasEmail = Boolean(emailKey);
                const status = statusInfo.status || 'not_requested';
                const statusLabel = getReferenceStatusLabel(status);
                const statusStyle = getReferenceStatusStyle(status);
                const statusIcon = getReferenceStatusIcon(status);
                const isVerified = (status || '').toLowerCase() === 'verified';
                const isRejected = (status || '').toLowerCase() === 'rejected';
                const buttonLabel = verifyingReferenceIndex === idx
                  ? 'Sending...'
                  : isVerified
                    ? 'Re-send Verification'
                    : 'Verify Reference';
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                    <div>
                      <strong style={{ color: '#333', fontSize: '15px' }}>{rf.referenceName || rf.name || 'Name'}</strong>
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>{rf.referenceJobTitle || rf.jobTitle || 'Job Title'}</p>
                    </div>
                    <div>
                      <p style={{ color: '#666', fontSize: '14px' }}>{rf.referenceCompany || rf.company || 'Company'}</p>
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>{rf.referenceRelationship || rf.relation || 'Relationship'}</p>
                    </div>
                    <div>
                      <p style={{ color: '#3b82f6', fontSize: '14px' }}>{rf.referenceEmail || rf.contact || 'Email'}</p>
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>{rf.referencePhone || 'Phone'}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end', textAlign: 'right' }}>
                      <span style={statusStyle}>
                        <FontAwesomeIcon icon={statusIcon} style={{ fontSize: '12px' }} />
                        {statusLabel}
                      </span>
                      {!isViewingOtherProfile && (
                        <button
                          onClick={() => handleVerifyReference(rf, idx)}
                          disabled={verifyingReferenceIndex === idx || !hasEmail}
                          style={{
                            padding: '8px 18px',
                            fontSize: '12px',
                            fontWeight: 600,
                            border: 'none',
                            borderRadius: '999px',
                            background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
                            color: 'white',
                            boxShadow: '0 6px 15px rgba(249, 115, 22, 0.25)',
                            cursor: verifyingReferenceIndex === idx || !hasEmail ? 'not-allowed' : 'pointer',
                            opacity: verifyingReferenceIndex === idx || !hasEmail ? 0.6 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            minWidth: '0',
                            width: 'auto'
                          }}
                        >
                          {buttonLabel}
                        </button>
                      )}
                      {!hasEmail && (
                        <small style={{ color: '#b91c1c', fontSize: '12px' }}>
                          Add an email to request verification.
                        </small>
                      )}
                      {isVerified && statusInfo.verifiedAt && (
                        <small style={{ color: '#15803d', fontSize: '12px' }}>
                          Verified on {new Date(statusInfo.verifiedAt).toLocaleDateString()}
                        </small>
                      )}
                      {isRejected && statusInfo.updatedAt && (
                        <small style={{ color: '#b91c1c', fontSize: '12px' }}>
                          Rejected on {new Date(statusInfo.updatedAt).toLocaleDateString()}
                        </small>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
          {(references || []).length === 0 && <div style={{ color: '#999' }}>No references added</div>}
          {editingSections.references && (
            <button
              onClick={() => {
                setReferences(prev => [
                  ...prev,
                  {
                    referenceName: '',
                    referenceJobTitle: '',
                    referenceCompany: '',
                    referenceRelationship: '',
                    referenceEmail: '',
                    referencePhone: ''
                  }
                ]);
              }}
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '15px'
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add Reference
            </button>
          )}
        </div>

        {/* Work Experience Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faBuilding} /> Work Experience</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.experience ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      console.log('🖊️ Edit clicked for Experience section');
                      setEditingSections(prev => {
                        console.log('📊 Previous editingSections:', prev);
                        const newState = { ...prev, experience: true };
                        console.log('📊 New editingSections:', newState);
                        return newState;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('experience');
                      setEditingSections(prev => ({ ...prev, experience: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setEditingSections(prev => ({ ...prev, experience: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>

          {/* Info Badge */}
          <div style={{ padding: '12px', background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '13px', color: '#0369a1', marginBottom: '20px' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px' }} />
            List all relevant work experience starting with the most recent
          </div>

          {(experience || []).map((ex, idx) => (
            <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', marginBottom: '15px', borderLeft: '4px solid #3b82f6', position: 'relative' }}>
              {editingSections.experience ? (
                <div style={{ display: 'grid', gap: '15px', paddingRight: '40px' }}>
                  {/* Job Title */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Job Title <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={ex.jobTitle || ex.title || ex.role || ex.positionTitle || ''}
                      onChange={(e) => {
                        const newExp = [...experience];
                        newExp[idx] = { ...newExp[idx], jobTitle: e.target.value, title: e.target.value, role: e.target.value, positionTitle: e.target.value };
                        setExperience(newExp);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., Senior Marketing Manager"
                      required
                    />
                  </div>

                  {/* Company Name, Company Location */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Company Name <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={ex.company || ''}
                      onChange={(e) => {
                        const newExp = [...experience];
                        newExp[idx] = { ...newExp[idx], company: e.target.value };
                        setExperience(newExp);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., ABC Corporation"
                        required
                    />
                  </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Company Location</label>
                      <input
                        type="text"
                        value={(() => {
                          const loc = ex.companyLocation || ex.location || '';
                          if (typeof loc === 'string') return loc;
                          if (loc && typeof loc === 'object') {
                            return `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '';
                          }
                          return '';
                        })()}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], companyLocation: e.target.value, location: e.target.value };
                          setExperience(newExp);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="City, Country"
                      />
                    </div>
                  </div>

                  {/* Employment Type, Industry */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Employment Type</label>
                      <select
                        value={ex.employmentType || 'full-time'}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], employmentType: e.target.value };
                          setExperience(newExp);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                      >
                        <option value="full-time">Full-time</option>
                        <option value="part-time">Part-time</option>
                        <option value="contract">Contract</option>
                        <option value="freelance">Freelance</option>
                        <option value="internship">Internship</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Industry</label>
                      <input
                        type="text"
                        list={`experience-industry-${idx}`}
                        value={ex.jobIndustry || ex.industry || ''}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], jobIndustry: e.target.value, industry: e.target.value };
                          setExperience(newExp);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="Select or type your industry"
                        autoComplete="off"
                      />
                      <datalist id={`experience-industry-${idx}`}>
                        <option value="Technology & IT" />
                        <option value="Finance & Banking" />
                        <option value="Healthcare & Medical" />
                        <option value="Education & Training" />
                        <option value="Retail & E-commerce" />
                        <option value="Manufacturing & Production" />
                        <option value="Construction & Real Estate" />
                        <option value="Hospitality & Tourism" />
                        <option value="Transportation & Logistics" />
                        <option value="Media & Entertainment" />
                        <option value="Telecommunications" />
                        <option value="Agriculture & Farming" />
                        <option value="Energy & Utilities" />
                        <option value="Legal Services" />
                        <option value="Consulting & Professional Services" />
                        <option value="FMCGs and CPGs" />
                        <option value="Government & Public Sector" />
                        <option value="Non-Profit & NGO" />
                        <option value="Automotive" />
                        <option value="Aerospace & Defense" />
                        <option value="Pharmaceutical & Biotech" />
                        <option value="Insurance" />
                        <option value="Real Estate" />
                        <option value="Food & Beverage" />
                        <option value="Fashion & Apparel" />
                      </datalist>
                    </div>
                  </div>

                  {/* Start Date, End Date */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Start Date <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="month"
                        value={ex.startDate || ''}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], startDate: e.target.value };
                          setExperience(newExp);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>End Date</label>
                      <input
                        type="month"
                        value={ex.endDate || ''}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], endDate: e.target.value };
                          setExperience(newExp);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        disabled={ex.currentJob || ex.currentlyWorking}
                      />
                    </div>
                  </div>

                  {/* Current Job Checkbox */}
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={ex.currentJob || false}
                        onChange={(e) => {
                          const newExp = [...experience];
                          newExp[idx] = { ...newExp[idx], currentJob: e.target.checked, endDate: e.target.checked ? '' : newExp[idx].endDate };
                          setExperience(newExp);
                        }}
                      />
                      <span>I currently work here</span>
                    </label>
                  </div>

                  {/* Key Responsibilities & Achievements */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Key Responsibilities & Achievements</label>
                    <textarea
                      value={ex.jobDescription || ex.description || ex.responsibilities || ''}
                      onChange={(e) => {
                        const newExp = [...experience];
                        newExp[idx] = { ...newExp[idx], jobDescription: e.target.value, description: e.target.value, responsibilities: e.target.value };
                        setExperience(newExp);
                      }}
                      style={{ ...formInputBase, width: '100%', minHeight: '100px' }}
                      placeholder="• Led team of 10 marketing professionals&#10;• Increased revenue by 45% through strategic campaigns&#10;• Managed $500K annual budget"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <h4>{ex.jobTitle || ex.title || ex.role || ex.positionTitle || 'Role'}</h4>
                  <div style={{ color: '#3b82f6', fontWeight: 600, marginBottom: '5px' }}>{ex.company || 'Company'}</div>
                  <div style={{ color: '#999', fontSize: '13px', marginBottom: '10px' }}>
                    {(ex.startDate || '') + (ex.endDate ? ` - ${ex.endDate}` : (ex.currentJob || ex.currentlyWorking ? ' - Present' : ''))}
                  </div>
                  {(ex.jobDescription || ex.description || ex.responsibilities) && (
                    <div style={{ color: '#666', marginTop: '10px', whiteSpace: 'pre-line' }}>
                      {ex.jobDescription || ex.description || ex.responsibilities}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          {(experience || []).length === 0 && <div style={{ color: '#999' }}>No experience added</div>}
          {editingSections.experience && (
            <button
              onClick={() => {
                setExperience(prev => [
                  ...prev,
                  {
                    jobTitle: '',
                    company: '',
                    startDate: '',
                    endDate: '',
                    currentJob: false,
                    jobDescription: ''
                  }
                ]);
              }}
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                marginTop: '10px'
              }}
            >
              + Add Experience
            </button>
          )}
        </div>

        {/* Education Section */}
        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faGraduationCap} /> Education</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.education ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      console.log('🖊️ Edit clicked for Education section');
                      setEditingSections(prev => {
                        console.log('📊 Previous editingSections:', prev);
                        const newState = { ...prev, education: true };
                        console.log('📊 New editingSections:', newState);
                        return newState;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('education');
                      setEditingSections(prev => ({ ...prev, education: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setEditingSections(prev => ({ ...prev, education: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {(education || []).map((ed, idx) => (
            <div key={idx} style={{ padding: '20px', background: '#f9fafb', borderRadius: '8px', marginBottom: '15px', borderLeft: '4px solid #3b82f6', position: 'relative' }}>
              {editingSections.education ? (
                <div style={{ display: 'grid', gap: '15px', paddingRight: '40px' }}>
                  {/* Degree/Certificate */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Degree/Certificate <span style={{ color: '#dc2626' }}>*</span></label>
                    <select
                      value={ed.degreeType || ed.degree || ''}
                      onChange={(e) => {
                        const newEd = [...education];
                        newEd[idx] = { ...newEd[idx], degreeType: e.target.value, degree: e.target.value };
                        setEducation(newEd);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      required
                    >
                      <option value="">Select degree type</option>
                      <option value="high-school">High School Diploma</option>
                      <option value="associate">Associate Degree</option>
                      <option value="bachelor">Bachelor's Degree</option>
                      <option value="master">Master's Degree</option>
                      <option value="phd">Doctorate/PhD</option>
                      <option value="professional">Professional Degree</option>
                      <option value="certificate">Certificate</option>
                      <option value="diploma">Diploma</option>
                    </select>
                  </div>

                  {/* Field of Study */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Field of Study <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={ed.fieldOfStudy || ed.field || ''}
                      onChange={(e) => {
                        const newEd = [...education];
                        newEd[idx] = { ...newEd[idx], fieldOfStudy: e.target.value, field: e.target.value };
                        setEducation(newEd);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., Computer Science, Business Administration"
                      required
                    />
                  </div>

                  {/* Institution Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Institution Name <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={ed.institution || ed.school || ''}
                      onChange={(e) => {
                        const newEd = [...education];
                        newEd[idx] = { ...newEd[idx], institution: e.target.value, school: e.target.value };
                        setEducation(newEd);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., University of Nairobi"
                      required
                    />
                  </div>

                  {/* Institution Location, Grade */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Institution Location</label>
                      <input
                        type="text"
                        value={(() => {
                          const loc = ed.institutionLocation || ed.location || '';
                          if (typeof loc === 'string') return loc;
                          if (loc && typeof loc === 'object') {
                            return `${loc.city || ''}, ${loc.country || ''}`.replace(/^,\s*|,\s*$/g, '').trim() || loc.address || '';
                          }
                          return '';
                        })()}
                        onChange={(e) => {
                          const newEd = [...education];
                          newEd[idx] = { ...newEd[idx], institutionLocation: e.target.value, location: e.target.value };
                          setEducation(newEd);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="City, Country"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Grade/GPA</label>
                      <input
                        type="text"
                        value={ed.grade || ed.gpa || ''}
                        onChange={(e) => {
                          const newEd = [...education];
                          newEd[idx] = { ...newEd[idx], grade: e.target.value, gpa: e.target.value };
                          setEducation(newEd);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="e.g., 3.8/4.0, First Class"
                      />
                    </div>
                  </div>

                  {/* Start Year, End Year */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Start Year <span style={{ color: '#dc2626' }}>*</span></label>
                      <input
                        type="number"
                        value={ed.eduStartYear || ed.startYear || ''}
                        onChange={(e) => {
                          const newEd = [...education];
                          newEd[idx] = { ...newEd[idx], eduStartYear: e.target.value, startYear: e.target.value };
                          setEducation(newEd);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="2018"
                        min="1950"
                        max="2030"
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>End Year (or Expected)</label>
                      <input
                        type="number"
                        value={ed.eduEndYear || ed.endYear || ''}
                        onChange={(e) => {
                          const newEd = [...education];
                          newEd[idx] = { ...newEd[idx], eduEndYear: e.target.value, endYear: e.target.value };
                          setEducation(newEd);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="2022"
                        min="1950"
                        max="2035"
                      />
                    </div>
                  </div>

                  {/* Activities & Achievements */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Activities & Achievements</label>
                    <textarea
                      value={ed.eduActivities || ed.activities || ''}
                      onChange={(e) => {
                        const newEd = [...education];
                        newEd[idx] = { ...newEd[idx], eduActivities: e.target.value, activities: e.target.value };
                        setEducation(newEd);
                      }}
                      style={{ ...formInputBase, width: '100%', minHeight: '80px' }}
                      placeholder="Academic honors, relevant coursework, extracurricular activities"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <h4>{ed.degreeType || ed.degree || 'Degree'}{ed.fieldOfStudy || ed.field ? ` in ${ed.fieldOfStudy || ed.field}` : ''}</h4>
                  <div style={{ color: '#3b82f6', fontWeight: 600, marginBottom: '5px' }}>{ed.institution || ed.school || 'Institution'}</div>
                  <div style={{ color: '#999', fontSize: '13px', marginBottom: '10px' }}>{(ed.eduStartYear || ed.startYear || '') + (ed.eduEndYear || ed.endYear ? ` - ${ed.eduEndYear || ed.endYear}` : '')}</div>
                </>
              )}
            </div>
          ))}
          {(education || []).length === 0 && <div style={{ color: '#999' }}>No education added</div>}
          {editingSections.education && (
            <button
              onClick={() => {
                setEducation(prev => [
                  ...prev,
                  {
                    degreeType: '',
                    fieldOfStudy: '',
                    institution: '',
                    eduStartYear: '',
                    eduEndYear: ''
                  }
                ]);
              }}
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                marginTop: '10px'
              }}
            >
              + Add Education
            </button>
          )}
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faCertificate} /> Certifications & Licenses</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.certifications ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      console.log('🖊️ Edit clicked for Certifications section');
                      setEditingSections(prev => {
                        console.log('📊 Previous editingSections:', prev);
                        const newState = { ...prev, certifications: true };
                        console.log('📊 New editingSections:', newState);
                        return newState;
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('certifications');
                      setEditingSections(prev => ({ ...prev, certifications: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setEditingSections(prev => ({ ...prev, certifications: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {(certifications || []).map((ct, idx) => (
            <div key={idx} style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', marginBottom: '12px', position: 'relative' }}>
              {editingSections.certifications ? (
                <div style={{ display: 'grid', gap: '15px', paddingRight: '40px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Certification Name</label>
                    <input
                      type="text"
                      value={ct.certificationName || ct.name || ''}
                      onChange={(e) => {
                        const newCert = [...certifications];
                        newCert[idx] = { ...newCert[idx], certificationName: e.target.value, name: e.target.value };
                        // Auto-verify if all fields are filled (optional)
                        const updatedCert = { ...newCert[idx], certificationName: e.target.value };
                        if (updatedCert.certificationName && updatedCert.certIssuer && updatedCert.certIssueDate && updatedCert.credentialId) {
                          newCert[idx].verificationStatus = 'Verified';
                        } else {
                          newCert[idx].verificationStatus = 'Pending Verification';
                        }
                        setCertifications(newCert);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., PMP, AWS Certified"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Issuing Organization</label>
                    <input
                      type="text"
                      value={ct.certIssuer || ct.issuer || ''}
                      onChange={(e) => {
                        const newCert = [...certifications];
                        newCert[idx] = { ...newCert[idx], certIssuer: e.target.value, issuer: e.target.value };
                        // Auto-verify if all fields are filled (optional)
                        const updatedCert = { ...newCert[idx], certIssuer: e.target.value };
                        if (updatedCert.certificationName && updatedCert.certIssuer && updatedCert.certIssueDate && updatedCert.credentialId) {
                          newCert[idx].verificationStatus = 'Verified';
                        } else {
                          newCert[idx].verificationStatus = 'Pending Verification';
                        }
                        setCertifications(newCert);
                      }}
                      style={{ ...formInputBase, width: '100%' }}
                      placeholder="e.g., Project Management Institute"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Issue Date</label>
                      <input
                        type="month"
                        value={ct.certIssueDate || ct.date || ''}
                        onChange={(e) => {
                          const newCert = [...certifications];
                          newCert[idx] = { ...newCert[idx], certIssueDate: e.target.value, date: e.target.value };
                          // Auto-verify if all fields are filled (optional)
                          const updatedCert = { ...newCert[idx], certIssueDate: e.target.value };
                          if (updatedCert.certificationName && updatedCert.certIssuer && updatedCert.certIssueDate && updatedCert.credentialId) {
                            newCert[idx].verificationStatus = 'Verified';
                          } else {
                            newCert[idx].verificationStatus = 'Pending Verification';
                          }
                          setCertifications(newCert);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Expiry Date</label>
                      <input
                        type="month"
                        value={ct.certExpiryDate || ''}
                        onChange={(e) => {
                          const newCert = [...certifications];
                          newCert[idx] = { ...newCert[idx], certExpiryDate: e.target.value };
                          setCertifications(newCert);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Credential ID</label>
                      <input
                        type="text"
                        value={ct.credentialId || ''}
                        onChange={(e) => {
                          const newCert = [...certifications];
                          newCert[idx] = { ...newCert[idx], credentialId: e.target.value };
                          // Auto-verify if all fields are filled (optional)
                          const updatedCert = { ...newCert[idx], credentialId: e.target.value };
                          if (updatedCert.certificationName && updatedCert.certIssuer && updatedCert.certIssueDate && updatedCert.credentialId) {
                            newCert[idx].verificationStatus = 'Verified';
                          } else {
                            newCert[idx].verificationStatus = 'Pending Verification';
                          }
                          setCertifications(newCert);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="Certificate number/ID (optional)"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '15px', padding: '12px', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#92400e' }}>
                      <strong>📋 To verify your certificate:</strong> Fill in all fields (Certification Name, Issuing Organization, Issue Date, and Credential ID). Your certificate will be automatically verified once all details are provided.
                    </p>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ marginBottom: '8px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
                      {ct.certificationName || ct.name || 'Certification'}
                    </h4>
                    <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 5px 0' }}>
                      <strong>Issuer:</strong> {ct.certIssuer || ct.issuer || 'N/A'}
                    </p>
                    <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 5px 0' }}>
                      <strong>Issued:</strong> {ct.certIssueDate || ct.date || 'N/A'} 
                      {ct.certExpiryDate && ` - Expires: ${ct.certExpiryDate}`}
                    </p>
                    {ct.credentialId && (
                      <p style={{ color: '#64748b', fontSize: '13px', margin: '5px 0 0 0', fontFamily: 'monospace' }}>
                        <strong>ID:</strong> {ct.credentialId}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                    <span className={`status-badge ${(ct.verificationStatus || 'pending verification').toLowerCase() === 'verified' ? 'status-offered' : 'status-pending'}`}>
                      {ct.verificationStatus || 'Pending Verification'}
                    </span>
                    {ct.credentialId && (
                      <button
                        onClick={() => {
                          // Verify certification logic
                          alert(`Verifying certificate:\n\nName: ${ct.certificationName}\nIssuer: ${ct.certIssuer}\nCredential ID: ${ct.credentialId}\n\nVerification feature coming soon!`);
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} /> Verify
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {(certifications || []).length === 0 && <div style={{ color: '#999' }}>No certifications added</div>}
          {editingSections.certifications && (
            <button
              onClick={() => {
                setCertifications(prev => [
                  ...prev,
                  {
                    certificationName: '',
                    certIssuer: '',
                    certIssueDate: '',
                    certExpiryDate: '',
                    verificationStatus: 'Pending Verification'
                  }
                ]);
              }}
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                marginTop: '10px'
              }}
            >
              + Add Certification
            </button>
          )}
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faLanguage} /> Languages</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.languages ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => setEditingSections(prev => ({ ...prev, languages: true }))}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('languages');
                      setEditingSections(prev => ({ ...prev, languages: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setEditingSections(prev => ({ ...prev, languages: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gap: '15px' }}>
            {(languages || []).map((lg, idx) => (
              <div key={idx} style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', position: 'relative' }}>
                {editingSections.languages ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', paddingRight: '40px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Language</label>
                      <input
                        type="text"
                        value={lg.language || ''}
                        onChange={(e) => {
                          const newLangs = [...languages];
                          newLangs[idx] = { ...newLangs[idx], language: e.target.value };
                          setLanguages(newLangs);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                        placeholder="e.g., English"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Proficiency</label>
                      <select
                        value={lg.proficiency || ''}
                        onChange={(e) => {
                          const newLangs = [...languages];
                          newLangs[idx] = { ...newLangs[idx], proficiency: e.target.value };
                          setLanguages(newLangs);
                        }}
                        style={{ ...formInputBase, width: '100%' }}
                      >
                        <option value="">Select Level</option>
                        <option value="native">Native/Bilingual</option>
                        <option value="fluent">Fluent</option>
                        <option value="advanced">Advanced</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="basic">Basic</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <>
                    <h4 style={{ marginBottom: '10px' }}>{lg.language || ''}</h4>
                    <p style={{ color: '#666' }}>{lg.proficiency || ''}</p>
                  </>
                )}
              </div>
            ))}
            {(languages || []).length === 0 && <div style={{ color: '#999' }}>No languages added</div>}
            {editingSections.languages && (
              <button
                onClick={() => {
                  setLanguages(prev => [
                    ...prev,
                    {
                      language: '',
                      proficiency: ''
                    }
                  ]);
                }}
                style={{
                  background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  marginTop: '10px'
                }}
              >
                + Add Language
              </button>
            )}
          </div>
        </div>

        <div className="card" style={{ ...elevatedCardStyle, padding: '25px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0' }}>
            <h3 className="card-title"><FontAwesomeIcon icon={faLink} /> Professional Online Presence</h3>
            {!isViewingOtherProfile && (
              <div style={{ display: 'flex', gap: '8px' }}>
                {!editingSections.socialLinks ? (
                  <button 
                    className="btn btn-primary"
                    onClick={() => setEditingSections(prev => ({ ...prev, socialLinks: true }))}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                <>
                  <button 
                    className="btn btn-success" 
                    onClick={() => {
                      saveSection('socialLinks');
                      setEditingSections(prev => ({ ...prev, socialLinks: false }));
                    }}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={() => setEditingSections(prev => ({ ...prev, socialLinks: false }))}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </>
              )}
              </div>
            )}
          </div>
          {editingSections.socialLinks ? (
            <div style={{ display: 'grid', gap: '15px' }}>
              <div className="form-group">
                <label className="form-label"><FontAwesomeIcon icon={faLink} /> LinkedIn Profile URL</label>
                <input 
                  type="url"
                  style={{ ...formInputBase, width: '100%' }} 
                  value={professionalLinks.linkedin || ''} 
                  onChange={(e) => setProfessionalLinks(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/your-profile"
                />
              </div>
              <div className="form-group">
                <label className="form-label"><FontAwesomeIcon icon={faLink} /> GitHub Profile URL</label>
                <input 
                  type="url"
                  style={{ ...formInputBase, width: '100%' }} 
                  value={professionalLinks.github || ''} 
                  onChange={(e) => setProfessionalLinks(prev => ({ ...prev, github: e.target.value }))}
                  placeholder="https://github.com/your-username"
                />
              </div>
              <div className="form-group">
                <label className="form-label"><FontAwesomeIcon icon={faLink} /> Portfolio URL</label>
                <input 
                  type="url"
                  style={{ ...formInputBase, width: '100%' }} 
                  value={professionalLinks.portfolio || ''} 
                  onChange={(e) => setProfessionalLinks(prev => ({ ...prev, portfolio: e.target.value }))}
                  placeholder="https://your-portfolio.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label"><FontAwesomeIcon icon={faLink} /> Personal Website URL</label>
                <input 
                  type="url"
                  style={{ ...formInputBase, width: '100%' }} 
                  value={professionalLinks.website || ''} 
                  onChange={(e) => setProfessionalLinks(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://your-website.com"
                />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {professionalLinks && typeof professionalLinks === 'object' && Object.entries(professionalLinks).map(([key, url]) => {
                if (!url) return null;
                const labels = {
                  linkedin: 'LinkedIn',
                  github: 'GitHub', 
                  portfolio: 'Portfolio',
                  website: 'Website'
                };
                const icons = {
                  linkedin: faLink,
                  github: faLink,
                  portfolio: faLink,
                  website: faLink
                };
                return (
                  <div key={key} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    padding: '12px 16px', 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%)',
                    border: '1px solid #e0e7ff',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '6px',
                      fontWeight: '600',
                      color: '#1e293b',
                      fontSize: '14px'
                    }}>
                      <FontAwesomeIcon icon={icons[key]} style={{ color: '#3b82f6' }} />
                      <span>{labels[key] || key}</span>
                    </div>
                    <a 
                      href={url} 
                      target="_blank" 
                      rel="noreferrer" 
                      style={{ 
                        color: '#3b82f6', 
                        textDecoration: 'none',
                        fontSize: '13px',
                        marginLeft: '24px',
                        wordBreak: 'break-all',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      {url}
                    </a>
                  </div>
                );
              })}
              {(!professionalLinks || Object.values(professionalLinks).every(v => !v)) && (
                <div style={{ color: '#999' }}>No professional links added</div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>

    {/* Location Dialog */}
    {locationDialogOpen && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
        onClick={closeLocationDialog}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Dialog Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '2px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            borderRadius: '12px 12px 0 0'
          }}>
            <h2 style={{
              margin: 0,
              color: '#ffffff',
              fontSize: '20px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              Edit Location & View Map
            </h2>
            <button
              onClick={closeLocationDialog}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                color: '#ffffff',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.transform = 'rotate(0deg)';
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Dialog Content */}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Google Maps Embed */}
            <div style={{ marginBottom: '20px' }}>
              <GoogleMapEmbed
                address={(() => {
                  const parts = [
                    locationDialogData.address,
                    locationDialogData.currentCity,
                    locationDialogData.residentCountry
                  ].filter(Boolean);
                  return parts.join(', ') || null;
                })()}
                latitude={locationDialogData.latitude}
                longitude={locationDialogData.longitude}
                height={300}
                label="Location Preview"
              />
            </div>

            {/* Location Form Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Full Address</label>
                <input
                  type="text"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.address}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Street address, building, apartment number"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Current City</label>
                <input
                  type="text"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.currentCity}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, currentCity: e.target.value }))}
                  placeholder="e.g., Nairobi"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Resident Country</label>
                <input
                  type="text"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.residentCountry}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, residentCountry: e.target.value }))}
                  placeholder="e.g., Kenya"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Postal/ZIP Code</label>
                <input
                  type="text"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.postalCode}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, postalCode: e.target.value }))}
                  placeholder="Enter postal code"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Latitude</label>
                <input
                  type="number"
                  step="any"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.latitude}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="e.g., -1.2921"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input
                  type="number"
                  step="any"
                  style={{ ...formInputBase, width: '100%' }}
                  value={locationDialogData.longitude}
                  onChange={(e) => setLocationDialogData(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="e.g., 36.8219"
                />
              </div>
            </div>

            {/* Dialog Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              paddingTop: '20px',
              borderTop: '2px solid #f0f0f0',
              marginTop: '10px'
            }}>
              <button
                onClick={closeLocationDialog}
                style={{
                  padding: '10px 24px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f3f4f6';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLocationDialogSave}
                disabled={saving}
                style={{
                  padding: '10px 24px',
                  background: saving ? '#9ca3af' : '#f97316',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.target.style.background = '#ea580c';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving) {
                    e.target.style.background = '#f97316';
                  }
                }}
              >
                <FontAwesomeIcon icon={faSave} />
                {saving ? 'Saving...' : 'Save Location'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    {showResumeUpload && (
      <ResumeUpload
        onResumeUploaded={handleResumeUploaded}
        onClose={() => setShowResumeUpload(false)}
      />
    )}
    </>
  );
};

export default MyProfile;


